<template>
  <div class="surat-keluar-create">
    <!-- Page Header -->
    <div class="page-header">
      <h2 class="page-title">Tambah Surat Keluar</h2>
    </div>

    <!-- Form Card -->
    <div class="form-card">
      <a-form :model="form" :rules="rules" ref="formRef" layout="vertical">
        <!-- Step 1: Tambah Surat Keluar Selection -->
        <div class="form-section">
          <label class="section-label">Tambah Surat Keluar <span class="required">*</span></label>
          <a-radio-group v-model:value="form.tambahSuratType" class="radio-group-horizontal">
            <a-radio value="buat_baru">Buat Surat Baru</a-radio>
            <a-radio value="arsipkan">Arsipkan Surat Keluar</a-radio>
          </a-radio-group>
        </div>

        <!-- Show SIMPAN button only when no selection made -->
        <div v-if="!form.tambahSuratType" class="submit-section initial-submit">
          <button disabled class="simpan-button">
            SIMPAN
          </button>
        </div>

        <!-- ==================== BUAT SURAT BARU FLOW ==================== -->
        <template v-if="form.tambahSuratType === 'buat_baru'">
          <!-- Jenis Surat Section -->
          <a-form-item
            label="Jenis Surat"
            required
            :validate-status="validationErrors.jenisSurat ? 'error' : ''"
            :help="validationErrors.jenisSurat"
          >
            <a-select
              v-model:value="form.jenisSurat"
              placeholder="Pilih Jenis Surat"
              class="full-width"
              show-search
              :filter-option="filterOption"
            >
              <a-select-option value="Surat Tugas" label="Surat Tugas">Surat Tugas</a-select-option>
              <a-select-option value="Cabang" label="Cabang">Cabang</a-select-option>
              <a-select-option value="Farmasi" label="Farmasi">Farmasi</a-select-option>
              <a-select-option value="Instansi Pemerintah" label="Instansi Pemerintah"
                >Instansi Pemerintah</a-select-option
              >
              <a-select-option value="Internal - Surat" label="Internal - Surat"
                >Internal - Surat</a-select-option
              >
              <a-select-option value="Internal - Undangan" label="Internal - Undangan"
                >Internal - Undangan</a-select-option
              >
              <a-select-option value="Kepegawaian PP" label="Kepegawaian PP"
                >Kepegawaian PP</a-select-option
              >
              <a-select-option value="MoU" label="MoU">MoU</a-select-option>
              <a-select-option value="PKS" label="PKS">PKS</a-select-option>
              <a-select-option value="OPKeseminatan Lain" label="OPKeseminatan Lain"
                >OPKeseminatan Lain</a-select-option
              >
              <a-select-option value="PB IDI" label="PB IDI">PB IDI</a-select-option>
              <a-select-option value="Perusahaan Swasta" label="Perusahaan Swasta"
                >Perusahaan Swasta</a-select-option
              >
              <a-select-option value="Podcast" label="Podcast">Podcast</a-select-option>
              <a-select-option value="Pokja" label="Pokja">Pokja</a-select-option>
              <a-select-option value="Rumah Sakit" label="Rumah Sakit">Rumah Sakit</a-select-option>
              <a-select-option value="SK" label="SK">SK</a-select-option>
              <a-select-option value="YJI" label="YJI">YJI</a-select-option>
              <a-select-option value="Lainnya" label="Lainnya">Lainnya</a-select-option>
            </a-select>
          </a-form-item>

          <!-- ==================== SURAT TUGAS SPECIFIC FIELDS ==================== -->
          <template v-if="form.jenisSurat === 'Surat Tugas'">
            <!-- Nomor Surat Section with increment/decrement -->
            <NomorSuratInput
              v-model="form.nomorSurat"
              :can-decrement="canDecrement"
              :error="nomorSuratError"
              :checking="nomorSuratChecking"
              @increment="() => incrementNomorSurat(form.jenisSurat)"
              @decrement="() => decrementNomorSurat(form.jenisSurat)"
            />

            <!-- Sifat Surat Section -->
            <SifatSuratRadio v-model="form.sifatSurat" />

            <!-- Bentuk Surat Section -->
            <BentukSuratRadio v-model="form.bentukSurat" />

            <!-- Surat Undangan Acara Section -->
            <div class="form-section">
              <label class="section-label label-grey">Surat Undangan Acara</label>
              <a-select
                v-model:value="form.suratUndanganAcaraId"
                placeholder="Pilih surat masuk terkait (Opsional)"
                :options="undanganAcaraOptions"
                class="full-width"
                allow-clear
                show-search
                :filter-option="filterOption"
              />
            </div>

            <!-- Two Column Layout -->
            <a-row :gutter="24">
              <a-col :xs="24" :md="12">
                <a-form-item
                  label="Nama Kegiatan"
                  required
                  :validate-status="validationErrors.namaKegiatan ? 'error' : ''"
                  :help="validationErrors.namaKegiatan"
                >
                  <a-input v-model:value="form.namaKegiatan" placeholder="Nama Kegiatan Dinas" />
                </a-form-item>
              </a-col>
              <a-col :xs="24" :md="12">
                <a-form-item
                  label="Waktu Perjalanan Mulai"
                  required
                  :validate-status="validationErrors.waktuPerjalananMulai ? 'error' : ''"
                  :help="validationErrors.waktuPerjalananMulai"
                >
                  <a-date-picker
                    v-model:value="form.waktuPerjalananMulai"
                    :show-time="{ format: 'HH:mm' }"
                    format="DD-MM-YYYY HH:mm"
                    placeholder="DD-MM-YYYY HH:MM"
                    class="full-width"
                  />
                </a-form-item>
              </a-col>
              <a-col :xs="24" :md="12">
                <a-form-item
                  label="Waktu Perjalanan Selesai"
                  :validate-status="validationErrors.waktuPerjalananSelesai ? 'error' : ''"
                  :help="validationErrors.waktuPerjalananSelesai"
                >
                  <a-date-picker
                    v-model:value="form.waktuPerjalananSelesai"
                    :show-time="{ format: 'HH:mm' }"
                    format="DD-MM-YYYY HH:mm"
                    placeholder="DD-MM-YYYY HH:MM"
                    class="full-width"
                    :disabled-date="
                      d =>
                        form.waktuPerjalananMulai
                          ? d && d.isBefore(form.waktuPerjalananMulai, 'day')
                          : false
                    "
                    @change="
                      val => {
                        if (
                          val &&
                          form.waktuPerjalananMulai &&
                          val.isBefore(form.waktuPerjalananMulai)
                        ) {
                          validationErrors.value.waktuPerjalananSelesai =
                            'Waktu selesai tidak boleh sebelum waktu mulai'
                        } else {
                          validationErrors.value.waktuPerjalananSelesai = ''
                        }
                      }
                    "
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <a-row :gutter="24">
              <a-col :xs="24" :md="12">
                <a-form-item
                  label="Tempat Kegiatan"
                  required
                  :validate-status="validationErrors.tempatKegiatan ? 'error' : ''"
                  :help="validationErrors.tempatKegiatan"
                >
                  <a-input
                    v-model:value="form.tempatKegiatan"
                    placeholder="Tempat kegiatan dinas"
                  />
                </a-form-item>
              </a-col>
              <a-col :xs="24" :md="12">
                <a-form-item
                  label="Tanggal Surat"
                  required
                  :validate-status="validationErrors.tanggalSurat ? 'error' : ''"
                  :help="validationErrors.tanggalSurat"
                >
                  <a-date-picker
                    v-model:value="form.tanggalSurat"
                    format="DD-MM-YYYY"
                    placeholder="Pilih tanggal surat"
                    class="full-width"
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <!-- Penandatangan Section -->
            <PenandatanganSelect
              v-model:kiri-value="form.penandatanganKiriId"
              v-model:kanan-value="form.penandatanganKananId"
              :kiri-error="validationErrors.penandatanganKiri"
            />

            <!-- Keperluan Section -->
            <a-form-item
              label="Keperluan"
              required
              :validate-status="validationErrors.keperluan ? 'error' : ''"
              :help="validationErrors.keperluan"
            >
              <a-textarea v-model:value="form.keperluan" placeholder="Keperluan dinas" :rows="3" />
            </a-form-item>

            <!-- Perwakilan Dinas Table -->
            <a-form-item
              :validate-status="validationErrors.perwakilanDinas ? 'error' : ''"
              :help="validationErrors.perwakilanDinas"
            >
              <PerwakilanDinasTable
                :data-source="form.perwakilanDinas"
                :show-pdf-button="false"
                @add="addPerwakilanRow"
                @remove="removePerwakilanRow"
                @preview="handlePreviewPerwakilan"
              />
            </a-form-item>

            <!-- Preview Button -->
            <!-- <div class="preview-section">
              <a-button type="default" class="btn-preview" disabled>
                <template #icon>
                  <EyeOutlined />
                </template>
                LIHAT PREVIEW SURAT TUGAS
              </a-button>
              <p class="preview-hint">Preview tersedia setelah surat disimpan</p> -->
            <!-- </div> -->
          </template>

          <!-- ==================== OTHER SURAT TYPES (All except Surat Tugas) ==================== -->
          <template v-if="form.jenisSurat !== 'Surat Tugas'">
            <!-- Metode Pembuatan Section -->
            <MetodePembuatanRadio v-model="form.metodePembuatan" />

            <!-- File Upload Section (only when Upload File is selected) -->
            <template v-if="isUploadFile">
              <div class="form-section upload-section">
                <label class="section-label"
                  >Upload Dokumen Surat <span class="required">*</span></label
                >
                <p class="section-description">
                  Upload file surat yang sudah jadi (sudah ditandatangani)
                </p>

                <!-- Custom Upload Area -->
                <div
                  class="upload-area"
                  @click="triggerMetodePembuatanFileInput"
                  @drop.prevent="handleMetodePembuatanFileDrop"
                  @dragover.prevent="handleMetodePembuatanDragOver"
                  @dragleave.prevent="handleMetodePembuatanDragLeave"
                  @dragenter.prevent
                >
                  <input
                    ref="metodePembuatanFileInputRef"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    multiple
                    style="display: none"
                    @change="handleMetodePembuatanFileChange"
                  />
                  <div class="upload-icon">
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 48 48"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                    >
                      <path
                        d="M24 8V32M24 8L16 16M24 8L32 16M40 32V40H8V32"
                        stroke="#CCCCCC"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <p class="upload-text">Klik atau seret file ke sini untuk mengunggah</p>
                  <p class="upload-hint">
                    Upload satu atau lebih file dalam format PDF, PNG, atau JPG/JPEG (maksimal 10MB
                    per file)
                  </p>
                </div>

                <!-- Uploaded Files Display -->
                <div v-if="uploadedFiles.length > 0" class="uploaded-files-list">
                  <div
                    v-for="(file, index) in uploadedFiles"
                    :key="file.uid || index"
                    class="uploaded-file-item"
                  >
                    <PaperClipOutlined class="file-icon" />
                    <a
                      :href="getMetodePembuatanFileUrl(file)"
                      target="_blank"
                      class="file-name-link"
                      >{{ file.name }}</a
                    >
                    <a-button
                      type="text"
                      danger
                      size="small"
                      @click="removeMetodePembuatanUploadedFile(index)"
                      class="btn-remove-file"
                    >
                      <template #icon>
                        <CloseOutlined />
                      </template>
                    </a-button>
                  </div>
                </div>
              </div>
            </template>

            <!-- Nomor Surat: Auto-generated for all except "Lainnya", Manual input for "Lainnya" -->
            <template v-if="form.jenisSurat === 'Lainnya'">
              <!-- Manual Input for Lainnya -->
              <a-form-item
                label="Nomor Surat"
                required
                :validate-status="
                  nomorSuratError || validationErrors.nomorSuratManual ? 'error' : ''
                "
                :help="nomorSuratError || validationErrors.nomorSuratManual"
              >
                <a-input
                  v-model:value="form.nomorSuratManual"
                  placeholder="Nomor surat keluar"
                  @blur="checkNomorSuratUnique"
                  @keypress.space.prevent
                  @input="form.nomorSuratManual = form.nomorSuratManual.replace(/\s+/g, '')"
                />
                <p
                  v-if="nomorSuratChecking"
                  class="checking-hint"
                  style="margin-top: 4px; margin-bottom: 0; color: #1890ff;"
                >
                  Memeriksa nomor surat...
                </p>
              </a-form-item>
            </template>
            <template v-else>
              <!-- Auto-generated for other types -->
              <NomorSuratInput
                v-model="form.nomorSurat"
                :can-decrement="canDecrement"
                :error="nomorSuratError"
                :checking="nomorSuratChecking"
                @increment="() => incrementNomorSurat(form.jenisSurat)"
                @decrement="() => decrementNomorSurat(form.jenisSurat)"
              />
            </template>

            <!-- Sifat Surat Section -->
            <SifatSuratRadio v-model="form.sifatSurat" />

            <!-- Bentuk Surat Section - hide when Upload File is selected -->
            <template v-if="!isUploadFile">
              <BentukSuratRadio v-model="form.bentukSurat" />
            </template>

            <!-- Surat Masuk Terkait Section -->
            <div class="form-section">
              <label class="section-label label-grey">Surat Masuk Terkait</label>
              <a-select
                v-model:value="form.suratUndanganAcaraId"
                placeholder="Pilih surat masuk terkait (Opsional)"
                :options="undanganAcaraOptions"
                class="full-width"
                allow-clear
                show-search
                :filter-option="filterOption"
              />
            </div>

            <!-- Tanggal Surat -->
            <a-form-item
              label="Tanggal Surat"
              required
              :validate-status="validationErrors.tanggalSurat ? 'error' : ''"
              :help="validationErrors.tanggalSurat"
            >
              <a-date-picker
                v-model:value="form.tanggalSurat"
                format="DD-MM-YYYY"
                placeholder="Pilih tanggal surat"
                class="full-width"
              />
            </a-form-item>

            <!-- Perihal Section -->
            <a-form-item
              label="Perihal"
              required
              :validate-status="validationErrors.perihal ? 'error' : ''"
              :help="validationErrors.perihal"
            >
              <a-input
                v-model:value="form.perihal"
                placeholder="Perihal surat yang akan ditampilkan di tabel list surat keluar"
              />
            </a-form-item>

            <!-- Penandatangan Section -->
            <PenandatanganSelect
              v-model:kiri-value="form.penandatanganKiriId"
              v-model:kanan-value="form.penandatanganKananId"
              :kiri-error="validationErrors.penandatanganKiri"
            />

            <!-- Isi Surat Section with TinyMCE - only show when NOT Upload File -->
            <template v-if="!isUploadFile">
              <a-form-item
                label="Isi Surat"
                required
                :validate-status="validationErrors.body ? 'error' : ''"
                :help="validationErrors.body"
              >
                <template #extra>
                  <p class="section-description" style="margin-top: 4px; margin-bottom: 8px;">
                    Isi bagian ini dengan teks yang ingin ditampilkan sebagai isi surat (tidak perlu
                    menulis kop surat dan area tanda tangan)
                  </p>
                </template>
                <div class="tinymce-container" :class="{ 'has-error': validationErrors.body }">
                  <Editor v-model="form.body" :api-key="apiKey" :init="suratLainnyaInit" />
                </div>
              </a-form-item>

              <!-- Preview Button -->
              <div class="preview-section">
                <a-button type="default" class="btn-preview" @click="handlePreviewLainnya">
                  <template #icon>
                    <EyeOutlined />
                  </template>
                  LIHAT PREVIEW SURAT
                </a-button>
              </div>
            </template>
          </template>

          <!-- Verifikator Section (Common for both) -->
          <a-form-item
            class="verifikator-form-item"
            :validate-status="validationErrors.verifikator ? 'error' : ''"
            :help="validationErrors.verifikator"
          >
            <VerifikatorTable
              :data-source="form.verifikator"
              @add="addVerifikatorRow"
              @remove="removeVerifikatorRow"
            />
          </a-form-item>

          <AlurVerifikasi class="alur-verifikasi-tight" :verifikator="form.verifikator" />

          <!-- Submit Buttons for Buat Surat Baru -->
          <div class="submit-section">
            <!-- <a-space size="large"> -->
            <button
              @click="handleSubmit"
              :loading="submitLoading"
              :disabled="form.jenisSurat === 'Lainnya' && !!nomorSuratError"
              class="simpan-button"
            >
              SIMPAN
            </button>
            <!-- </a-space> -->
            <button
              class="btn-draft"
              @click="handleSaveAsDraft"
              :loading="draftLoading"
              :disabled="form.jenisSurat === 'Lainnya' && !!nomorSuratError"
            >
              SIMPAN SEBAGAI DRAFT
            </button>
          </div>
        </template>

        <!-- ==================== ARSIPKAN SURAT KELUAR FLOW ==================== -->
        <template v-if="form.tambahSuratType === 'arsipkan'">
          <!-- Dokumen Surat Upload Section - Match Surat Masuk style -->
          <div class="form-section">
            <label class="section-label"><span class="required">*</span> Dokumen Surat</label>
            <p class="section-description">
              Mohon scan surat fisik terlebih dahulu dengan scanner agar mudah dibaca
            </p>

            <!-- Custom Upload Area matching Surat Masuk style -->
            <div
              class="upload-area"
              @click="triggerFileInput"
              @drop.prevent="handleFileDrop"
              @dragover.prevent="handleDragOver"
              @dragleave.prevent="handleDragLeave"
              @dragenter.prevent
            >
              <input
                ref="fileInputRef"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                style="display: none"
                multiple
                @change="handleFileChange"
              />
              <div class="upload-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="36"
                  height="36"
                  viewBox="0 0 36 36"
                  fill="none"
                >
                  <path
                    d="M35.0457 14.6766L35.0363 14.6391L29.7769 1.275C29.5425 0.520312 28.8441 0 28.0519 0H6.73316C5.93629 0 5.22848 0.529688 5.00348 1.29375L0.0862889 14.5219L0.072227 14.5547L0.0628514 14.5922C0.00191392 14.8219 -0.0168361 15.0562 0.0159764 15.2859C0.0112889 15.3609 0.00660156 15.4359 0.00660156 15.5109V32.6625C0.00784108 33.418 0.308506 34.1422 0.842716 34.6764C1.37693 35.2106 2.10112 35.5113 2.8566 35.5125H32.2566C33.8269 35.5125 35.1066 34.2328 35.1113 32.6625V15.5109C35.1113 15.45 35.1113 15.3891 35.1066 15.3375C35.1254 15.1078 35.1066 14.8875 35.0457 14.6766ZM21.18 12.6609L21.166 13.3969C21.1285 15.5016 19.6754 16.9172 17.5519 16.9172C16.516 16.9172 15.6254 16.5844 14.9832 15.9516C14.341 15.3187 13.9894 14.4375 13.9707 13.3969L13.9566 12.6609H4.30973L8.03629 3.6H26.7488L30.5785 12.6609H21.18ZM3.60191 16.2609H10.9754C12.1144 18.9375 14.5379 20.5172 17.5566 20.5172C19.1363 20.5172 20.6035 20.0766 21.7894 19.2422C22.83 18.5109 23.641 17.4891 24.166 16.2609H31.5019V31.9125H3.60191V16.2609Z"
                    fill="#E10F28"
                  />
                </svg>
              </div>
              <p class="upload-text">Klik atau seret file ke sini untuk mengunggah</p>
              <p class="upload-hint">Unggah file dalam format PDF, PNG, atau JPG/JPEG</p>
            </div>

            <!-- Uploaded Files List -->
            <div v-if="arsipForm.fileList.length > 0" class="uploaded-files-list">
              <div
                v-for="(file, index) in arsipForm.fileList"
                :key="file.uid"
                class="uploaded-file-item"
              >
                <PaperClipOutlined class="file-icon" />
                <a :href="getFileObjectUrl(file)" target="_blank" class="file-name-link">{{
                  file.name
                }}</a>
                <button
                  type="button"
                  class="delete-file-button"
                  @click.prevent="removeFileFromList(index)"
                  title="Hapus file"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path
                      d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                    ></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- AI Auto-Detect Button -->
          <div v-if="arsipForm.fileList.length > 0" class="form-section ai-section">
            <a-button
              type="default"
              class="btn-ai-detect"
              :disabled="arsipForm.suratList.length === 0 || isDetecting"
              :loading="isDetecting"
              @click="autoDetectWithAI"
            >
              <template #icon v-if="!isDetecting">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <mask
                    id="mask0_1239_4989"
                    style="mask-type: alpha"
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="20"
                    height="20"
                  >
                    <rect width="20" height="20" fill="#D9D9D9" />
                  </mask>
                  <g mask="url(#mask0_1239_4989)">
                    <path
                      d="M4.0625 17.021L3 15.9585L9.08333 9.87516L5.5 9.00016L9.375 6.5835L9.04167 2.021L12.5417 4.9585L16.75 3.25016L15.0417 7.47933L18 10.9585L13.4381 10.6252L11.0208 14.521L10.1458 10.9377L4.0625 17.021ZM4.5 7.00016L3 5.50016L4.5 4.00016L6 5.50016L4.5 7.00016ZM11.6042 10.6877L12.625 9.06266L14.5417 9.2085L13.306 7.746L14.0269 5.9585L12.2556 6.68766L10.7917 5.4585L10.9375 7.37516L9.29167 8.38329L11.1531 8.836L11.6042 10.6877ZM14.5 17.0002L13 15.5002L14.5 14.0002L16 15.5002L14.5 17.0002Z"
                      fill="currentColor"
                    />
                  </g>
                </svg>
              </template>
              {{ isDetecting ? 'MENDETEKSI...' : 'AUTO-DETEKSI ISI SURAT' }}
            </a-button>
            <a-tooltip placement="right">
              <template #title>
                <div style="max-width: 300px">
                  Fitur Auto-Deteksi dengan AI dapat mengisi field Jenis Surat, Nomor Surat, Tanggal
                  Surat, dan Penandatangan secara otomatis. Mohon upload file surat dalam format PDF
                  atau scan surat fisik sejelas mungkin.
                </div>
              </template>
              <QuestionCircleOutlined class="info-icon" />
            </a-tooltip>
          </div>

          <!-- Loading State for AI Detection -->
          <div v-if="isDetecting" class="ai-loading-section">
            <div class="ai-loading-content">
              <div class="loading-spinner"></div>
              <h3 class="ai-loading-title">Mohon tunggu</h3>
              <p class="ai-loading-text">
                Sistem sedang membaca isi surat dan mengisi formulir tambah surat keluar
              </p>
            </div>
          </div>

          <!-- Multiple Surat Forms - Hidden during AI detection -->
          <template v-if="!isDetecting">
            <div
              v-for="(surat, suratIndex) in arsipForm.suratList"
              :key="surat.id"
              class="uploaded-surat-section"
            >
              <!-- Surat Header -->
              <div class="surat-header">
                <h3 class="surat-title">Surat {{ suratIndex + 1 }}</h3>
              </div>

              <!-- File Attachment - Styled like Surat Masuk -->
              <div v-if="surat.fileRef" class="file-attachment">
                <PaperClipOutlined class="attachment-icon" />
                <a :href="surat.fileRef.url" target="_blank" class="file-link">{{
                  surat.fileRef.name
                }}</a>
              </div>

              <!-- Section Title -->
              <h4 class="section-title">Detail Surat</h4>

              <!-- Jenis Surat & Sifat Surat Row -->
              <a-row :gutter="16">
                <a-col :xs="24" :sm="24" :md="12" :lg="12">
                  <a-form-item
                    label="Jenis Surat"
                    required
                    :validate-status="
                      validationErrors.arsipSurat?.[suratIndex]?.jenisSurat ? 'error' : ''
                    "
                    :help="validationErrors.arsipSurat?.[suratIndex]?.jenisSurat"
                  >
                    <a-select
                      v-model:value="surat.jenisSurat"
                      placeholder="Pilih Jenis Surat"
                      size="large"
                      show-search
                      :filter-option="filterOption"
                    >
                      <a-select-option value="Surat Tugas" label="Surat Tugas"
                        >Surat Tugas</a-select-option
                      >
                      <a-select-option value="Cabang" label="Cabang">Cabang</a-select-option>
                      <a-select-option value="Farmasi" label="Farmasi">Farmasi</a-select-option>
                      <a-select-option value="Instansi Pemerintah" label="Instansi Pemerintah"
                        >Instansi Pemerintah</a-select-option
                      >
                      <a-select-option value="Internal - Surat" label="Internal - Surat"
                        >Internal - Surat</a-select-option
                      >
                      <a-select-option value="Internal - Undangan" label="Internal - Undangan"
                        >Internal - Undangan</a-select-option
                      >
                      <a-select-option value="Kepegawaian PP" label="Kepegawaian PP"
                        >Kepegawaian PP</a-select-option
                      >
                      <a-select-option value="MoU" label="MoU">MoU</a-select-option>
                      <a-select-option value="PKS" label="PKS">PKS</a-select-option>
                      <a-select-option value="OPKeseminatan Lain" label="OPKeseminatan Lain"
                        >OPKeseminatan Lain</a-select-option
                      >
                      <a-select-option value="PB IDI" label="PB IDI">PB IDI</a-select-option>
                      <a-select-option value="Perusahaan Swasta" label="Perusahaan Swasta"
                        >Perusahaan Swasta</a-select-option
                      >
                      <a-select-option value="Podcast" label="Podcast">Podcast</a-select-option>
                      <a-select-option value="Pokja" label="Pokja">Pokja</a-select-option>
                      <a-select-option value="Rumah Sakit" label="Rumah Sakit"
                        >Rumah Sakit</a-select-option
                      >
                      <a-select-option value="SK" label="SK">SK</a-select-option>
                      <a-select-option value="YJI" label="YJI">YJI</a-select-option>
                      <a-select-option value="Lainnya" label="Lainnya">Lainnya</a-select-option>
                    </a-select>
                  </a-form-item>
                </a-col>
                <a-col :xs="24" :sm="24" :md="12" :lg="12">
                  <a-form-item label="Sifat Surat" required>
                    <a-radio-group v-model:value="surat.sifatSurat" size="large">
                      <a-radio value="Biasa">Biasa</a-radio>
                      <a-radio value="Terbatas">Terbatas</a-radio>
                      <a-radio value="Rahasia">Rahasia</a-radio>
                      <a-radio value="Sangat Rahasia">Sangat Rahasia</a-radio>
                    </a-radio-group>
                  </a-form-item>
                </a-col>
              </a-row>

              <!-- Bentuk Surat Section -->
              <a-form-item
                label="Bentuk Surat"
                required
                :validate-status="
                  validationErrors.arsipSurat?.[suratIndex]?.bentukSurat ? 'error' : ''
                "
                :help="validationErrors.arsipSurat?.[suratIndex]?.bentukSurat"
              >
                <a-radio-group v-model:value="surat.bentukSurat">
                  <div class="bentuk-surat-option">
                    <a-radio value="Soft copy">Soft copy</a-radio>
                    <p class="option-description">
                      Menggunakan tanda tangan dan stempel digital. Surat dapat diunduh di website
                      ini (format PDF).
                    </p>
                  </div>
                  <div class="bentuk-surat-option">
                    <a-radio value="Hard copy">Hard copy</a-radio>
                    <p class="option-description">
                      Menggunakan tanda tangan dan stempel basah. Surat dapat diambil di Sekretariat
                      PERKI.
                    </p>
                  </div>
                </a-radio-group>
              </a-form-item>

              <!-- Nomor Surat & Surat Masuk Terkait -->
              <a-row :gutter="16">
                <a-col :xs="24" :sm="24" :md="12" :lg="12">
                  <a-form-item
                    label="Nomor Surat"
                    required
                    :validate-status="
                      validationErrors.arsipSurat?.[suratIndex]?.nomorSurat ? 'error' : ''
                    "
                    :help="validationErrors.arsipSurat?.[suratIndex]?.nomorSurat"
                  >
                    <a-input
                      v-model:value="surat.nomorSurat"
                      placeholder="Nomor surat keluar"
                      size="large"
                      @keypress.space.prevent
                      @input="surat.nomorSurat = surat.nomorSurat.replace(/\s+/g, '')"
                    />
                  </a-form-item>
                </a-col>
                <a-col :xs="24" :sm="24" :md="12" :lg="12">
                  <a-form-item label="Surat Masuk Terkait">
                    <a-select
                      v-model:value="surat.suratMasukTerkaitId"
                      placeholder="Pilih surat masuk terkait (Opsional)"
                      :options="undanganAcaraOptions"
                      size="large"
                      allow-clear
                      show-search
                      :filter-option="filterOption"
                    />
                  </a-form-item>
                </a-col>
              </a-row>

              <!-- Tanggal Surat & Perihal -->
              <a-row :gutter="16">
                <a-col :xs="24" :sm="24" :md="12" :lg="12">
                  <a-form-item
                    label="Tanggal Surat"
                    required
                    :validate-status="
                      validationErrors.arsipSurat?.[suratIndex]?.tanggalSurat ? 'error' : ''
                    "
                    :help="validationErrors.arsipSurat?.[suratIndex]?.tanggalSurat"
                  >
                    <a-date-picker
                      v-model:value="surat.tanggalSurat"
                      format="DD-MM-YYYY"
                      placeholder="Pilih tanggal surat"
                      size="large"
                      style="width: 100%"
                    />
                  </a-form-item>
                </a-col>
                <a-col :xs="24" :sm="24" :md="12" :lg="12">
                  <a-form-item label="Perihal">
                    <a-input
                      v-model:value="surat.perihal"
                      placeholder="Perihal surat yang akan ditampilkan di tabel list surat keluar"
                      size="large"
                    />
                  </a-form-item>
                </a-col>
              </a-row>

              <!-- Penandatangan 1 & Penandatangan 2 -->
              <a-row :gutter="16">
                <a-col :xs="24" :sm="24" :md="12" :lg="12">
                  <a-form-item
                    label="Penandatangan 1 (Sisi Kiri)"
                    required
                    :validate-status="
                      validationErrors.arsipSurat?.[suratIndex]?.penandatanganId ? 'error' : ''
                    "
                    :help="validationErrors.arsipSurat?.[suratIndex]?.penandatanganId"
                  >
                    <a-select
                      v-model:value="surat.penandatanganId"
                      placeholder="Cari nama pengurus"
                      :options="pengurusOptionsFiltered"
                      size="large"
                      show-search
                      :filter-option="filterOption"
                    />
                  </a-form-item>
                </a-col>
                <a-col :xs="24" :sm="24" :md="12" :lg="12">
                  <a-form-item label="Penandatangan 2 (Sisi Kanan)">
                    <a-select
                      v-model:value="surat.penandatanganKananId"
                      placeholder="Cari nama pengurus"
                      :options="pengurusOptionsFiltered"
                      size="large"
                      allow-clear
                      show-search
                      :filter-option="filterOption"
                    />
                  </a-form-item>
                </a-col>
              </a-row>

              <!-- Status Pengiriman -->
              <a-row :gutter="16">
                <a-col :xs="24" :sm="24" :md="12" :lg="12">
                  <a-form-item
                    label="Status Pengiriman Surat"
                    required
                    :validate-status="
                      validationErrors.arsipSurat?.[suratIndex]?.statusPengiriman ? 'error' : ''
                    "
                    :help="validationErrors.arsipSurat?.[suratIndex]?.statusPengiriman"
                  >
                    <a-radio-group v-model:value="surat.statusPengiriman" size="large">
                      <a-radio value="Belum dikirim">Belum dikirim</a-radio>
                      <a-radio value="Sudah dikirim">Sudah dikirim</a-radio>
                    </a-radio-group>
                  </a-form-item>
                </a-col>

                <a-col :xs="24" :sm="24" :md="12" :lg="12">
                  <a-form-item
                    v-if="surat.statusPengiriman === 'Sudah dikirim'"
                    label="Bukti Pengiriman"
                    required
                  >
                    <a-button class="btn-pill-red-outline" @click="openBuktiModal(suratIndex)">
                      <PlusOutlined /> Tambah Foto
                    </a-button>
                  </a-form-item>
                </a-col>
              </a-row>

              <div
                v-if="
                  surat.statusPengiriman === 'Sudah dikirim' &&
                    surat.buktiFiles &&
                    surat.buktiFiles.length > 0
                "
                class="bukti-files-display"
              >
                <div class="uploaded-files-list">
                  <div
                    v-for="(file, fileIndex) in surat.buktiFiles"
                    :key="file.uid || fileIndex"
                    class="uploaded-file-item"
                  >
                    <PaperClipOutlined class="file-icon" />
                    <a :href="getFileObjectUrl(file)" target="_blank" class="file-name-link">
                      {{ file.name }}
                    </a>

                    <button
                      type="button"
                      class="delete-file-button"
                      @click.prevent="removeBuktiFile(suratIndex, fileIndex)"
                      title="Hapus file"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path
                          d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                        ></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Delete Surat Button - Styled like Surat Masuk
            <a-form-item>
              <button
                type="button"
                class="hapus-surat-button"
                @click.prevent="removeArsipSurat(suratIndex)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7V16C5 16.5304 5.21071 17.0391 5.58579 17.4142C5.96086 17.7893 6.46957 18 7 18H13C13.5304 18 14.0391 17.7893 14.4142 17.4142C14.7893 17.0391 15 16.5304 15 16V7M16 5H4M8 3H12"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                HAPUS SURAT
              </button>
            </a-form-item> -->
            </div>
          </template>

          <!-- Submit Button for Arsipkan -->
          <div class="submit-section">
            <a-form-item>
              <a-space size="large">
                <button
                  @click="handleSubmitArsip"
                  :loading="submitLoading"
                  :disabled="arsipForm.suratList.length === 0"
                  class="simpan-button"
                >
                  SIMPAN
                </button>
              </a-space>
            </a-form-item>
          </div>
        </template>
      </a-form>
    </div>

    <!-- Loading Overlay -->
    <div v-if="pageLoading" class="loading-overlay">
      <a-spin size="large" />
    </div>

    <!-- Submit Confirmation Modal -->
    <a-modal
      v-model:visible="submitModalVisible"
      :footer="null"
      :mask-closable="false"
      centered
      class="confirm-modal"
    >
      <template #title>
        <strong>Yakin isi formulir sudah sesuai?</strong>
      </template>
      <p class="modal-description">
        Mohon cek kembali data surat sebelum menyimpan formulir. Surat keluar akan dibuat dalam
        sistem dan dikirim ke verifikator atau penandatangan untuk diproses (jika ada).
      </p>
      <div class="modal-buttons">
        <a-button type="default" class="btn-pill-black" @click="submitModalVisible = false">
          KEMBALI
        </a-button>
        <a-button
          type="primary"
          class="btn-pill-red"
          :loading="submitLoading"
          @click="confirmSubmit"
        >
          YA, SIMPAN
        </a-button>
      </div>
    </a-modal>

    <!-- Draft Confirmation Modal -->
    <a-modal
      v-model:visible="draftModalVisible"
      :footer="null"
      :mask-closable="false"
      centered
      class="confirm-modal"
    >
      <template #title>
        <strong>Simpan surat keluar sebagai draft?</strong>
      </template>
      <p class="modal-description">
        Anda dapat mengubah atau menghapus draft surat nanti. Draft surat tidak akan dikirim ke
        verifikator atau penandatangan.
      </p>
      <div class="modal-buttons">
        <a-button class="btn-pill-black" @click="draftModalVisible = false">
          KEMBALI
        </a-button>
        <a-button class="btn-pill-red" :loading="draftLoading" @click="confirmSaveAsDraft">
          YA, SIMPAN
        </a-button>
      </div>
    </a-modal>
    <!-- Confirmation Modal for Arsipkan Surat -->
    <a-modal
      v-model:visible="arsipModalVisible"
      :footer="null"
      :mask-closable="false"
      centered
      class="confirm-modal"
    >
      <template #title>
        <strong>Yakin isi formulir sudah sesuai?</strong>
      </template>
      <p class="modal-description">
        Mohon cek kembali data surat sebelum menyimpan formulir.
        <strong>Data surat keluar yang sudah diarsipkan tidak dapat diubah kembali.</strong>
      </p>
      <div class="modal-buttons">
        <a-button type="default" class="btn-pill-black" @click="arsipModalVisible = false">
          KEMBALI
        </a-button>
        <a-button class="btn-pill-red" :loading="submitArsipLoading" @click="confirmSubmitArsip">
          YA, SIMPAN
        </a-button>
      </div>
    </a-modal>

    <!-- PDF Preview Modal -->
    <PDFPreviewModal
      v-model:visible="pdfModalVisible"
      :title="pdfModalTitle"
      :pdf-path="pdfModalPath"
    />

    <a-modal
      v-model:visible="buktiModalVisible"
      title="Upload Bukti Pengiriman"
      :width="600"
      @ok="handleBuktiModalOk"
      @cancel="handleBuktiModalCancel"
      ok-text="Simpan"
      cancel-text="Batal"
      class="bukti-pengiriman-modal"
      :ok-button-props="{ class: 'btn-pill-red' }"
      :cancel-button-props="{ class: 'btn-pill-black' }"
    >
      <p style="margin-bottom: 16px; color: #666;">
        Upload foto atau dokumen sebagai bukti pengiriman surat.
      </p>

      <a-upload
        v-model:file-list="currentBuktiFiles"
        :before-upload="beforeBuktiUpload"
        :multiple="true"
        accept=".pdf,.jpg,.jpeg,.png"
        list-type="picture"
      >
        <a-button class="btn-pill-red"> <PlusOutlined /> Pilih File </a-button>
      </a-upload>
    </a-modal>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, onMounted, computed, watch, h, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { notification } from 'ant-design-vue'
import {
  EyeOutlined,
  PaperClipOutlined,
  QuestionCircleOutlined,
  PlusOutlined,
  CloseOutlined,
} from '@ant-design/icons-vue'
import { suratKeluarAPI, documentExtractionAPI, suratMasukAPI } from '@/services/axios/perki'
import dayjs from 'dayjs'

// Import components
import {
  VerifikatorTable,
  AlurVerifikasi,
  NomorSuratInput,
  SifatSuratRadio,
  BentukSuratRadio,
  MetodePembuatanRadio,
  PenandatanganSelect,
  PerwakilanDinasTable,
  PDFPreviewModal,
} from '@/components/SuratKeluar'

// Import composables
import {
  useNomorSurat,
  useSuratKeluarValidation,
  useDateFormatting,
  useTinyMCE,
} from '@/composables'
import { useUnsavedChangesWarning } from '@/composables/useUnsavedChangesWarning'

// Import TinyMCE Editor component
import Editor from '@tinymce/tinymce-vue'

export default defineComponent({
  name: 'SuratKeluarCreate',
  components: {
    EyeOutlined,
    PaperClipOutlined,
    QuestionCircleOutlined,
    CloseOutlined,
    AlurVerifikasi,
    VerifikatorTable,
    NomorSuratInput,
    SifatSuratRadio,
    BentukSuratRadio,
    MetodePembuatanRadio,
    PenandatanganSelect,
    PerwakilanDinasTable,
    PlusOutlined,
    Editor, // Register TinyMCE Editor component
    PDFPreviewModal,
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const store = useStore()
    const formRef = ref(null)
    const fileInputRef = ref(null) // For custom file upload
    const pageLoading = ref(true)
    const isLoading = ref(false) // For preview PDF loading
    const submitArsipLoading = ref(false) // For arsip submission loading
    const isDetecting = ref(false)
    const isDragOver = ref(false) // For drag and drop styling

    // Modal visibility
    const submitModalVisible = ref(false)
    const draftModalVisible = ref(false)
    const arsipModalVisible = ref(false)

    // Bukti pengiriman modal state
    const buktiModalVisible = ref(false)
    const currentBuktiSuratIndex = ref(null)
    const currentBuktiFiles = ref([])

    // PDF Preview Modal state
    const pdfModalVisible = ref(false)
    const pdfModalTitle = ref('Preview Surat Keluar')
    const pdfModalPath = ref('')
    let currentBlobUrl = null // Track current blob URL for cleanup

    // Validation errors state
    const validationErrors = ref({})

    // Use composables
    const {
      nomorSurat: nomorSuratRef,
      canDecrement,
      fetchLatestNomorSurat,
      incrementNomorSurat: incrementNomorSuratComposable,
      decrementNomorSurat: decrementNomorSuratComposable,
      generateNomorSurat,
      resetNomorSurat,
    } = useNomorSurat()

    const {
      nomorSuratError,
      nomorSuratChecking,
      rules,
      checkNomorSuratUnique: checkNomorSuratUniqueComposable,
      clearNomorSuratError,
      validateSuratTugas,
      validateSuratLainnya,
    } = useSuratKeluarValidation()

    const { formatForAPI, formatDateTimeForAPI } = useDateFormatting()

    // TinyMCE setup
    const { apiKey, suratLainnyaInit } = useTinyMCE()

    // Unsaved changes warning
    const hasFormChanges = computed(() => {
      if (form.tambahSuratType === 'buat_baru') {
        return (
          form.jenisSurat !== null ||
          form.nomorSurat !== '' ||
          form.sifatSurat !== 'Biasa' ||
          form.bentukSurat !== 'Soft copy' ||
          form.namaKegiatan !== '' ||
          form.waktuPerjalananMulai !== null ||
          form.waktuPerjalananSelesai !== null ||
          form.tempatKegiatan !== '' ||
          form.tanggalSurat !== null ||
          form.penandatanganKiriId !== null ||
          form.penandatanganKananId !== null ||
          form.keperluan !== '' ||
          form.body !== '' ||
          form.perwakilanDinas.some(p => p.userId || p.namaNonPengurus) ||
          form.verifikator.some(v => v.userId)
        )
      } else if (form.tambahSuratType === 'arsipkan') {
        return (
          arsipForm.fileList.length > 0 ||
          arsipForm.suratList.some(
            s => s.jenisSurat || s.nomorSurat || s.tanggalSurat || s.penandatanganId,
          )
        )
      }
      return false
    })
    const { setHasUnsavedChanges, resetUnsavedChanges } = useUnsavedChangesWarning({
      hasFormChanges,
    })

    // Get loading states from store
    const submitLoading = computed(() => store.state.suratKeluar.submitLoading)
    const draftLoading = computed(() => store.state.suratKeluar.draftLoading)

    // Get pengurus options from store
    const pengurusOptions = computed(() => store.state.suratKeluar.pengurusOptions)

    // Filtered pengurus options - exclude Admin and Pekerja Harian roles for penandatangan and verifikator
    const pengurusOptionsFiltered = computed(() => {
      const options = store.state.suratKeluar.pengurusOptions || []
      const pengurusMap = store.state.suratKeluar.pengurusMap || {}
      return options.filter(option => {
        const user = pengurusMap[option.value]
        // Exclude admin and pekerja_harian roles from penandatangan/verifikator selections
        if (user && user.role) {
          const role = user.role.toLowerCase()
          return role !== 'admin' && role !== 'super_admin' && role !== 'pekerja_harian'
        }
        return true
      })
    })

    // Undangan Acara options (Surat Masuk references)
    const undanganAcaraOptions = computed(() => store.state.suratKeluar.suratMasukOptions)

    // Flag to prevent watchers from interfering with duplication logic
    const isDuplicating = ref(false)

    // Generate temporary ID for new rows
    function generateTempId() {
      return (
        'temp-' +
        Date.now() +
        '-' +
        Math.random()
          .toString(36)
          .substr(2, 9)
      )
    }

    // Form data for Buat Surat Baru
    const form = reactive({
      tambahSuratType: null,
      jenisSurat: 'Surat Tugas',
      nomorSurat: '', // Auto-generated for all except "Lainnya"
      nomorSuratManual: '', // Manual input for "Lainnya" only
      sifatSurat: 'Biasa',
      bentukSurat: 'Soft copy',
      metodePembuatan: 'Buat di Sistem', // New field for creation method
      suratUndanganAcaraId: null,
      namaKegiatan: '', // Used for Surat Tugas activity name
      waktuPerjalananMulai: null,
      waktuPerjalananSelesai: null,
      tempatKegiatan: '',
      tanggalSurat: null,
      penandatanganKiriId: null,
      penandatanganKananId: null,
      keperluan: '',
      perihal: '', // Used for other surat types (title/subject)
      body: '', // For all types except Surat Tugas (CKEditor content - HTML body)
      perwakilanDinas: [
        {
          id: generateTempId(),
          userId: null,
          isNonPengurus: false,
          searchValue: '',
          namaNonPengurus: null,
          jabatanNonPengurus: null,
          npaNonPengurus: null,
        },
      ],
      verifikator: [{ id: generateTempId(), userId: null, urutan: 1 }],
    })

    // Uploaded files for "Upload File" metode pembuatan
    const uploadedFiles = ref([])

    // Computed: Check if this is NOT Surat Tugas (for Metode Pembuatan section)
    const isNotSuratTugas = computed(() => {
      return form.jenisSurat && form.jenisSurat !== 'Surat Tugas'
    })

    // Computed: Check if Upload File method is selected
    const isUploadFile = computed(() => {
      return form.metodePembuatan === 'Upload File'
    })

    // Form data for Arsipkan Surat Keluar
    const arsipForm = reactive({
      fileList: [],
      suratList: [],
      fileList: [],
      suratList: [],
    })

    // Watch for tambahSuratType changes to reset form
    watch(
      () => form.tambahSuratType,
      async newValue => {
        // Skip if we're in duplication mode - don't reset the form
        if (isDuplicating.value) return

        if (newValue === 'buat_baru') {
          form.jenisSurat = 'Surat Tugas'
          resetNomorSurat()
          // Fetch latest nomor surat from backend and update form
          await fetchLatestNomorSurat('Surat Tugas')
          form.nomorSurat = nomorSuratRef.value
        }
      },
    )

    // Watch for jenisSurat changes to fetch appropriate nomor surat
    watch(
      () => form.jenisSurat,
      async (newValue, oldValue) => {
        if (!oldValue) return // Skip initial load
        // Skip if we're in duplication mode - don't reset the fields
        if (isDuplicating.value) return

        // Reset error when switching
        clearNomorSuratError()

        if (newValue === 'Lainnya') {
          // For Lainnya, clear auto-generated nomor and prepare manual input
          nomorSuratRef.value = ''
          form.nomorSurat = ''
          form.nomorSuratManual = ''
        } else {
          // For all other types (including Surat Tugas), fetch and generate nomor
          await fetchLatestNomorSurat(newValue)
          form.nomorSurat = nomorSuratRef.value
          form.nomorSuratManual = ''
        }

        // Reset type-specific fields
        if (newValue === 'Surat Tugas') {
          // Reset Surat Tugas specific fields
          form.namaKegiatan = ''
          form.waktuPerjalananMulai = null
          form.waktuPerjalananSelesai = null
          form.tempatKegiatan = ''
          form.keperluan = ''
          form.perihal = ''
          form.body = ''
        } else {
          // Reset other types fields
          form.perihal = ''
          form.body = ''
          form.namaKegiatan = ''
          form.waktuPerjalananMulai = null
          form.waktuPerjalananSelesai = null
          form.tempatKegiatan = ''
          form.keperluan = ''
          form.suratUndanganAcaraId = null
        }
      },
    )

    // Watch for modal close to cleanup blob URL
    watch(pdfModalVisible, isVisible => {
      if (!isVisible && currentBlobUrl) {
        // Clean up blob URL when modal closes
        setTimeout(() => {
          if (currentBlobUrl) {
            window.URL.revokeObjectURL(currentBlobUrl)
            currentBlobUrl = null
          }
        }, 100) // Small delay to ensure modal animation completes
      }
    })

    // Create empty arsip surat object
    function createEmptyArsipSurat() {
      return {
        id: generateTempId(),
        fileRef: null,
        jenisSurat: 'Lainnya',
        jenisSuratLainnya: '',
        sifatSurat: 'Biasa',
        bentukSurat: 'Soft copy',
        nomorSurat: '',
        suratMasukTerkaitId: null,
        tanggalSurat: null,
        penandatanganId: null,
        statusPengiriman: 'Belum dikirim',
        perihal: '',
        waktuPenerimaan: null,
      }
    }

    // Add perwakilan row
    const addPerwakilanRow = () => {
      form.perwakilanDinas.push({
        id: generateTempId(),
        userId: null,
        isNonPengurus: false,
        searchValue: '',
        namaNonPengurus: null,
        jabatanNonPengurus: null,
        npaNonPengurus: null,
      })
    }

    // Remove perwakilan row
    const removePerwakilanRow = index => {
      if (form.perwakilanDinas.length > 1) {
        form.perwakilanDinas.splice(index, 1)
      } else {
        notification.warning({ message: 'Minimal harus ada 1 perwakilan dinas' })
      }
    }

    // Add verifikator row
    const addVerifikatorRow = () => {
      const nextUrutan = form.verifikator.length + 1
      form.verifikator.push({
        id: generateTempId(),
        userId: null,
        urutan: nextUrutan,
      })
    }

    // Remove verifikator row
    const removeVerifikatorRow = index => {
      form.verifikator.splice(index, 1)
      form.verifikator.forEach((item, idx) => {
        item.urutan = idx + 1
      })
    }

    // ==================== FILE UPLOAD HANDLERS FOR METODE PEMBUATAN ====================
    // Ref for file input (metode pembuatan upload)
    const metodePembuatanFileInputRef = ref(null)

    // Trigger file input click
    const triggerMetodePembuatanFileInput = () => {
      metodePembuatanFileInputRef.value?.click()
    }

    // Handle drag over
    const handleMetodePembuatanDragOver = e => {
      e.currentTarget.classList.add('drag-over')
    }

    // Handle drag leave
    const handleMetodePembuatanDragLeave = e => {
      e.currentTarget.classList.remove('drag-over')
    }

    // Handle file drop
    const handleMetodePembuatanFileDrop = e => {
      e.currentTarget.classList.remove('drag-over')
      const files = Array.from(e.dataTransfer.files)
      addMetodePembuatanFilesToList(files)
    }

    // Handle file change (from input)
    const handleMetodePembuatanFileChange = e => {
      const files = Array.from(e.target.files)
      addMetodePembuatanFilesToList(files)
      // Reset input so same file can be selected again
      e.target.value = ''
    }

    // Add files to upload list with validation
    const addMetodePembuatanFilesToList = files => {
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg']
      const maxSize = 10 * 1024 * 1024 // 10MB

      files.forEach(file => {
        // Validate file type
        if (!validTypes.includes(file.type)) {
          notification.error({
            message: 'File tidak valid',
            description: `${file.name} bukan format PDF, PNG, atau JPG/JPEG`,
          })
          return
        }

        // Validate file size
        if (file.size > maxSize) {
          notification.error({
            message: 'File terlalu besar',
            description: `${file.name} melebihi batas 10MB`,
          })
          return
        }

        // Add to list with unique id
        uploadedFiles.value.push({
          uid: `${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`,
          name: file.name,
          file: file, // Store the actual File object
        })
      })
    }

    // Remove uploaded file
    const removeMetodePembuatanUploadedFile = index => {
      uploadedFiles.value.splice(index, 1)
    }

    // Get file URL for preview
    const getMetodePembuatanFileUrl = file => {
      if (file.file) {
        return URL.createObjectURL(file.file)
      }
      return '#'
    }

    // Wrapper functions for nomor surat composable
    const incrementNomorSurat = async jenisSurat => {
      // Clear previous error
      clearNomorSuratError()

      // Increment the number
      incrementNomorSuratComposable(jenisSurat)
      form.nomorSurat = nomorSuratRef.value

      // Check if this nomor is already used by another surat
      await checkNomorSuratUniqueComposable(form.nomorSurat)
    }

    const decrementNomorSurat = async jenisSurat => {
      // Clear previous error
      clearNomorSuratError()

      // Decrement the number
      decrementNomorSuratComposable(jenisSurat)
      form.nomorSurat = nomorSuratRef.value

      // Check if this nomor is already used by another surat
      await checkNomorSuratUniqueComposable(form.nomorSurat)
    }

    // Wrapper for checkNomorSuratUnique to work with form (for "Lainnya" manual input)
    const checkNomorSuratUnique = async () => {
      await checkNomorSuratUniqueComposable(form.nomorSuratManual)
    }

    // Filter option for select
    const filterOption = (input, option) => {
      return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
    }

    // Upload handlers for Arsipkan flow
    const beforeUpload = file => {
      const isAllowedType = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'].includes(
        file.type,
      )
      if (!isAllowedType) {
        notification.error({ message: 'Hanya file PDF, PNG, atau JPG yang diperbolehkan!' })
        return false
      }

      // Add new surat form for each uploaded file
      const newSurat = createEmptyArsipSurat()
      newSurat.fileRef = {
        uid: file.uid, // Store Ant Design's unique file ID for accurate matching
        name: file.name,
        url: URL.createObjectURL(file),
        file: file,
      }
      arsipForm.suratList.push(newSurat)

      // Add file to fileList manually so it shows in the upload dragger
      arsipForm.fileList.push({
        uid: file.uid,
        name: file.name,
        status: 'done',
        originFileObj: file,
      })

      return false // Prevent auto upload to server
    }

    const handleRemoveFile = file => {
      // Find and remove the corresponding surat by file uid
      const index = arsipForm.suratList.findIndex(s => s.fileRef?.uid === file.uid)
      if (index > -1) {
        // Revoke the object URL to free memory
        if (arsipForm.suratList[index].fileRef?.url) {
          URL.revokeObjectURL(arsipForm.suratList[index].fileRef.url)
        }
        arsipForm.suratList.splice(index, 1)
      }

      // Also remove from fileList (Ant Design should handle this, but just in case)
      const fileListIndex = arsipForm.fileList.findIndex(f => f.uid === file.uid)
      if (fileListIndex > -1) {
        arsipForm.fileList.splice(fileListIndex, 1)
      }

      return true // Allow Ant Design to remove from its internal list
    }

    // Custom file input trigger
    const triggerFileInput = () => {
      if (fileInputRef.value) {
        fileInputRef.value.click()
      }
    }

    // Handle file input change
    const handleFileChange = event => {
      const files = event.target.files
      if (files && files.length > 0) {
        for (const file of files) {
          processUploadedFile(file)
        }
      }
      // Reset input so same file can be selected again
      if (fileInputRef.value) {
        fileInputRef.value.value = ''
      }
    }

    // Handle drag over
    const handleDragOver = event => {
      event.preventDefault()
      isDragOver.value = true
    }

    // Handle drag leave
    const handleDragLeave = event => {
      event.preventDefault()
      isDragOver.value = false
    }

    // Handle file drop
    const handleFileDrop = event => {
      event.preventDefault()
      isDragOver.value = false
      const files = event.dataTransfer.files
      if (files && files.length > 0) {
        for (const file of files) {
          processUploadedFile(file)
        }
      }
    }

    // Process uploaded file (shared by both drag-drop and file input)
    const processUploadedFile = file => {
      const isAllowedType = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'].includes(
        file.type,
      )
      if (!isAllowedType) {
        notification.error({ message: 'Hanya file PDF, PNG, atau JPG yang diperbolehkan!' })
        return
      }

      // Generate unique ID for the file
      const uid = `file-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`

      // Add new surat form for each uploaded file
      const newSurat = createEmptyArsipSurat()
      newSurat.fileRef = {
        uid: uid,
        name: file.name,
        url: URL.createObjectURL(file),
        file: file,
      }
      arsipForm.suratList.push(newSurat)

      // Add file to fileList for display
      arsipForm.fileList.push({
        uid: uid,
        name: file.name,
        status: 'done',
        originFileObj: file,
      })
    }

    // Remove file from file list (called from file list UI)
    const removeFileFromList = index => {
      const file = arsipForm.fileList[index]
      if (file) {
        handleRemoveFile(file)
      }
    }

    // Get object URL for file preview
    const getFileObjectUrl = file => {
      if (file.originFileObj) {
        return URL.createObjectURL(file.originFileObj)
      }
      if (file.url) {
        return file.url
      }
      return '#'
    }

    // Open bukti modal for specific surat
    const openBuktiModal = suratIndex => {
      const surat = arsipForm.suratList[suratIndex]
      if (!surat) return

      currentBuktiSuratIndex.value = suratIndex
      // Load existing files if any
      currentBuktiFiles.value = surat.buktiFiles ? [...surat.buktiFiles] : []
      buktiModalVisible.value = true
    }

    // Before upload validation
    const beforeBuktiUpload = file => {
      const isValidSize = file.size <= 10 * 1024 * 1024 // 10MB
      const isValidType = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(
        file.type,
      )

      if (!isValidSize) {
        notification.warning({
          message: 'Ukuran file terlalu besar',
          description: `File ${file.name} melebihi 10MB`,
        })
        return false
      }
      if (!isValidType) {
        notification.warning({
          message: 'Format file tidak didukung',
          description: `File ${file.name} harus berformat PDF, JPG, atau PNG`,
        })
        return false
      }

      return false // Prevent auto upload, we handle it manually
    }

    // Handle bukti modal OK button
    const handleBuktiModalOk = () => {
      if (currentBuktiSuratIndex.value === null) return

      const surat = arsipForm.suratList[currentBuktiSuratIndex.value]
      if (!surat) return

      // Save the files to the surat
      surat.buktiFiles = [...currentBuktiFiles.value]

      notification.success({
        message: 'File berhasil disimpan',
        description: `${currentBuktiFiles.value.length} file bukti pengiriman siap diunggah`,
      })

      // Close modal and reset
      buktiModalVisible.value = false
      currentBuktiSuratIndex.value = null
      currentBuktiFiles.value = []
    }

    // Handle bukti modal cancel
    const handleBuktiModalCancel = () => {
      buktiModalVisible.value = false
      currentBuktiSuratIndex.value = null
      currentBuktiFiles.value = []
    }

    // Helper function to find best matching penandatangan from pengurusOptions
    const findMatchingPenandatangan = extractedName => {
      if (!extractedName || !pengurusOptions.value || pengurusOptions.value.length === 0) {
        return null
      }

      const normalizedExtracted = extractedName.toLowerCase().trim()

      // Remove common titles/prefixes for better matching
      const cleanName = name => {
        return name
          .toLowerCase()
          .replace(/^(dr\.?|prof\.?|ir\.?|drs\.?|drg\.?|h\.?|hj\.?)\s*/gi, '')
          .replace(
            /,?\s*(m\.?d\.?|ph\.?d\.?|sp\.?[a-z]*|m\.?sc\.?|m\.?m\.?|m\.?kes\.?|s\.?h\.?|s\.?e\.?|s\.?pd\.?|m\.?pd\.?|fiha|fesc|facc|fscai|fascc)\.?/gi,
            '',
          )
          .trim()
      }

      const cleanedExtracted = cleanName(normalizedExtracted)

      // Try exact match first
      for (const option of pengurusOptions.value) {
        const optionLabel = option.label.toLowerCase()
        if (optionLabel === normalizedExtracted || cleanName(optionLabel) === cleanedExtracted) {
          return option.value
        }
      }

      // Try partial match (extracted name contains or is contained in option)
      for (const option of pengurusOptions.value) {
        const cleanedOption = cleanName(option.label.toLowerCase())
        if (cleanedOption.includes(cleanedExtracted) || cleanedExtracted.includes(cleanedOption)) {
          return option.value
        }
      }

      // Try word-by-word matching (at least 2 words match)
      const extractedWords = cleanedExtracted.split(/\s+/).filter(w => w.length > 2)
      for (const option of pengurusOptions.value) {
        const optionWords = cleanName(option.label.toLowerCase())
          .split(/\s+/)
          .filter(w => w.length > 2)
        const matchingWords = extractedWords.filter(w =>
          optionWords.some(ow => ow.includes(w) || w.includes(ow)),
        )
        if (
          matchingWords.length >= 2 ||
          (extractedWords.length === 1 && matchingWords.length === 1)
        ) {
          return option.value
        }
      }

      return null
    }

    // Auto-detect document content using AI
    const autoDetectWithAI = async () => {
      if (arsipForm.suratList.length === 0) {
        notification.warning({ message: 'Mohon upload file terlebih dahulu' })
        return
      }

      isDetecting.value = true
      let successCount = 0
      let errorCount = 0
      const allMissingFields = []

      try {
        // Process each surat for AI extraction
        for (let i = 0; i < arsipForm.suratList.length; i++) {
          const surat = arsipForm.suratList[i]

          // Skip if no file reference
          if (!surat.fileRef || !surat.fileRef.file) {
            continue
          }

          try {
            // Create FormData for the file
            const formData = new FormData()
            formData.append('file', surat.fileRef.file)

            // Call backend API to extract document info
            const response = await documentExtractionAPI.extractForSuratKeluar(formData)

            if (response.data && response.data.data) {
              const extractedData = response.data.data
              const missingFields = []

              // List of valid jenis surat values
              const validJenisSurat = [
                'Surat Tugas',
                'Cabang',
                'Farmasi',
                'Instansi Pemerintah',
                'Internal - Surat',
                'Internal - Undangan',
                'Kepegawaian PP',
                'MoU',
                'PKS',
                'OPKeseminatan Lain',
                'PB IDI',
                'Perusahaan Swasta',
                'Podcast',
                'Pokja',
                'Rumah Sakit',
                'SK',
                'YJI',
                'Lainnya',
              ]

              // Map extracted data to form fields - ONLY fill empty fields
              // Jenis Surat
              if (extractedData.jenis_surat && extractedData.jenis_surat.trim() !== '') {
                if (!surat.jenisSurat) {
                  const extractedJenis = extractedData.jenis_surat.trim()
                  // Try to match with valid jenis surat (case-insensitive)
                  const matchedJenis = validJenisSurat.find(
                    j => j.toLowerCase() === extractedJenis.toLowerCase(),
                  )
                  if (matchedJenis) {
                    surat.jenisSurat = matchedJenis
                  } else {
                    // If no match, default to Lainnya
                    surat.jenisSurat = 'Lainnya'
                  }
                }
              } else {
                missingFields.push('Jenis Surat')
              }

              // Nomor Surat - only fill if empty
              if (extractedData.no_surat && extractedData.no_surat.trim() !== '') {
                if (!surat.nomorSurat || surat.nomorSurat.trim() === '') {
                  surat.nomorSurat = extractedData.no_surat
                }
              } else {
                missingFields.push('Nomor Surat')
              }

              // Tanggal Surat - only fill if empty
              if (extractedData.tanggal_surat && extractedData.tanggal_surat.trim() !== '') {
                if (!surat.tanggalSurat) {
                  surat.tanggalSurat = dayjs(extractedData.tanggal_surat)
                }
              } else {
                missingFields.push('Tanggal Surat')
              }

              // Penandatangan - only fill if empty, auto-match from pengurusOptions
              if (extractedData.penandatangan && extractedData.penandatangan.trim() !== '') {
                if (!surat.penandatanganId) {
                  const matchedId = findMatchingPenandatangan(extractedData.penandatangan)
                  if (matchedId) {
                    surat.penandatanganId = matchedId
                  } else {
                    missingFields.push('Penandatangan (tidak ditemukan kecocokan)')
                  }
                }
              } else {
                missingFields.push('Penandatangan')
              }

              // Store missing fields for this file
              if (missingFields.length > 0) {
                allMissingFields.push({
                  fileName: surat.fileRef.name,
                  suratIndex: i + 1,
                  fields: missingFields,
                })
              }

              successCount++
            }
          } catch (fileError) {
            console.error(`Error extracting file ${surat.fileRef.name}:`, fileError)
            errorCount++
          }
        }

        // Show result message with details about missing fields
        if (successCount > 0 && errorCount === 0) {
          if (allMissingFields.length === 0) {
            notification.success({
              message: 'Berhasil',
              description: `Formulir berhasil diisi otomatis. Mohon cek kembali.`,
            })
          } else {
            notification.warning({
              message: 'Deteksi Sebagian Berhasil',
              description: `Deteksi berhasil untuk ${successCount} surat, namun beberapa field tidak terdeteksi. Mohon lengkapi secara manual.`,
            })
          }
        } else if (successCount > 0 && errorCount > 0) {
          notification.warning({
            message: 'Deteksi Sebagian Gagal',
            description: `Deteksi berhasil untuk ${successCount} surat, ${errorCount} surat gagal diproses.`,
          })
        } else {
          notification.error({
            message: 'Gagal',
            description: 'Gagal melakukan deteksi otomatis untuk semua surat',
          })
        }
      } catch (error) {
        console.error('Error auto-detecting:', error)
        notification.error({
          message: 'Gagal',
          description: 'Gagal melakukan deteksi otomatis',
        })
      } finally {
        isDetecting.value = false
      }
    }

    // Remove arsip surat (with two-way sync - also removes from fileList)
    const removeArsipSurat = index => {
      const surat = arsipForm.suratList[index]
      if (surat && surat.fileRef) {
        // Revoke the object URL to free memory
        if (surat.fileRef.url) {
          URL.revokeObjectURL(surat.fileRef.url)
        }
        // Also remove from fileList (two-way sync)
        const fileListIndex = arsipForm.fileList.findIndex(f => f.uid === surat.fileRef.uid)
        if (fileListIndex > -1) {
          arsipForm.fileList.splice(fileListIndex, 1)
        }
      }
      arsipForm.suratList.splice(index, 1)
    }

    // Prepare form data for API
    const prepareFormData = (isDraft = false) => {
      const isSuratTugas = form.jenisSurat === 'Surat Tugas'
      const isLainnya = form.jenisSurat === 'Lainnya'

      // Determine nomor surat: auto-generated for all except "Lainnya"
      const nomorSuratValue = isLainnya ? form.nomorSuratManual : form.nomorSurat

      // If Upload File is selected, set bentukSurat to Hard copy
      const bentukSuratValue = isUploadFile.value ? 'Hard copy' : form.bentukSurat

      const baseData = {
        jenisSurat: form.jenisSurat,
        nomorSurat: nomorSuratValue,
        sifatSurat: form.sifatSurat,
        bentukSurat: bentukSuratValue,
        metodePembuatan: form.metodePembuatan,
        tanggalSurat: formatForAPI(form.tanggalSurat),
        penandatanganKiriId: form.penandatanganKiriId,
        penandatanganKananId: form.penandatanganKananId || null,
        verifikatorIds: form.verifikator.filter(item => item.userId).map(item => item.userId),
        isDraft: isDraft,
      }

      // If Upload File is selected and there are files, return FormData
      if (isUploadFile.value && uploadedFiles.value.length > 0) {
        const formData = new FormData()

        // Append all base data
        Object.keys(baseData).forEach(key => {
          const value = baseData[key]
          if (value !== null && value !== undefined) {
            if (Array.isArray(value)) {
              // For arrays, append each item
              value.forEach((item, index) => {
                formData.append(`${key}[${index}]`, item)
              })
            } else {
              formData.append(key, value)
            }
          }
        })

        // Append files
        uploadedFiles.value.forEach((fileObj, index) => {
          if (fileObj.file) {
            formData.append('files', fileObj.file)
          }
        })

        // Append additional fields for non-Surat Tugas
        if (!isSuratTugas) {
          formData.append('suratUndanganAcaraId', form.suratUndanganAcaraId || '')
          formData.append('namaKegiatan', form.perihal || form.jenisSurat)
        }

        return formData
      }

      // Otherwise return JSON
      if (isSuratTugas) {
        // Surat Tugas specific fields
        return {
          ...baseData,
          suratUndanganAcaraId: form.suratUndanganAcaraId || null,
          namaKegiatan: form.namaKegiatan,
          waktuPerjalananMulai: formatDateTimeForAPI(form.waktuPerjalananMulai),
          waktuPerjalananSelesai: formatDateTimeForAPI(form.waktuPerjalananSelesai),
          tempatKegiatan: form.tempatKegiatan,
          keperluan: form.keperluan,
          perwakilanDinas: form.perwakilanDinas
            .filter(item => item.userId || item.isNonPengurus)
            .map(item => {
              if (item.isNonPengurus) {
                return {
                  isNonPengurus: true,
                  namaNonPengurus: item.namaNonPengurus,
                  jabatanNonPengurus: item.jabatanNonPengurus,
                  npaNonPengurus: item.npaNonPengurus,
                }
              } else {
                return {
                  userId: item.userId,
                  isNonPengurus: false,
                }
              }
            }),
        }
      } else {
        // All other types (including Lainnya) - uses rich text body
        return {
          ...baseData,
          suratUndanganAcaraId: form.suratUndanganAcaraId || null,
          namaKegiatan: form.perihal || form.jenisSurat, // perihal as title
          body: form.body || null,
          perwakilanDinas: [], // No perwakilan for non-Surat Tugas types
        }
      }
    }

    // Handle preview for Lainnya
    const handlePreviewLainnya = async () => {
      // Only validate body content - preview should work even without nomor, tanggal, or penandatangan
      if (!form.body || form.body.trim() === '' || form.body === '<p></p>') {
        notification.warning({
          message: 'Preview Tidak Dapat Ditampilkan',
          description: 'Mohon isi konten surat terlebih dahulu',
          duration: 4,
        })
        return
      }

      // Prepare preview data for backend (use placeholder values if not filled)
      const previewData = {
        jenisSurat: form.jenisSurat || 'SURAT LAINNYA',
        nomorSurat: form.nomorSuratManual || form.nomorSurat || '[NOMOR SURAT]',
        sifatSurat: form.sifatSurat,
        bentukSurat: form.bentukSurat,
        namaKegiatan: form.perihal || form.jenisSurat || 'Surat Lainnya',
        tanggalSurat: form.tanggalSurat
          ? form.tanggalSurat.format('DD-MM-YYYY')
          : dayjs().format('DD-MM-YYYY'),
        penandatanganKiriId: form.penandatanganKiriId || null,
        penandatanganKananId: form.penandatanganKananId || null,
        body: form.body,
      }

      try {
        // Call backend API to generate PDF
        const response = await suratKeluarAPI.previewLainnyaPDF(previewData)

        // Create blob URL for the PDF
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const blobUrl = window.URL.createObjectURL(blob)

        // Cleanup previous blob URL if exists
        if (currentBlobUrl) {
          window.URL.revokeObjectURL(currentBlobUrl)
        }
        currentBlobUrl = blobUrl

        // Prevent race condition: close modal → wait → set path → wait → open modal
        // This ensures only the isVisible watcher fires (not both isVisible + pdfPath watchers)
        pdfModalVisible.value = false
        await nextTick()

        pdfModalTitle.value = `Preview Surat - ${form.perihal ||
          form.jenisSuratLainnya ||
          'Surat Lainnya'}`
        pdfModalPath.value = blobUrl
        await nextTick()

        pdfModalVisible.value = true
      } catch (error) {
        console.error('Preview Lainnya PDF error:', error)
        notification.error({
          message: 'Gagal Membuat Preview',
          description:
            error.response?.data?.message || 'Terjadi kesalahan saat membuat preview PDF',
          duration: 5,
        })
      }
    }

    // Handle preview for Perwakilan Dinas row
    const handlePreviewPerwakilan = async ({ index, record }) => {
      // Validate: must have either userId (pengurus) or non-pengurus data
      if (!record.userId && !record.isNonPengurus) {
        notification.warning({ message: 'Silakan pilih perwakilan dinas terlebih dahulu' })
        return
      }

      // For non-pengurus, validate required fields
      if (record.isNonPengurus) {
        if (!record.namaNonPengurus || !record.jabatanNonPengurus || !record.npaNonPengurus) {
          notification.warning({
            message: 'Data non-pengurus belum lengkap',
            description: 'Silakan lengkapi Nama, Jabatan, dan NPA untuk non-pengurus',
          })
          return
        }
      }

      // Minimal validation - only require perwakilan to be selected
      // Preview will show whatever fields are currently filled
      if (!form.namaKegiatan) {
        notification.warning({
          message: 'Nama Kegiatan diperlukan',
          description: 'Minimal isi Nama Kegiatan untuk melihat preview surat',
        })
        return
      }
      isLoading.value = false

      try {
        isLoading.value = true

        // Prepare preview data - send whatever is currently filled
        const previewData = {
          jenisSurat: form.jenisSurat || 'Surat Tugas',
          nomorSurat: form.nomorSurat || '[NOMOR BELUM DIISI]',
          sifatSurat: form.sifatSurat || 'Biasa',
          bentukSurat: form.bentukSurat || 'Soft Copy',
          tanggalSurat: form.tanggalSurat ? form.tanggalSurat.format('DD-MM-YYYY') : null,
          namaKegiatan: form.namaKegiatan,
          waktuPerjalananMulai: formatDateTimeForAPI(form.waktuPerjalananMulai),
          waktuPerjalananSelesai: formatDateTimeForAPI(form.waktuPerjalananSelesai),
          tempatKegiatan: form.tempatKegiatan || null,
          keperluan: form.keperluan || null,
          penandatanganKiriId: form.penandatanganKiriId || null,
          penandatanganKananId: form.penandatanganKananId || null,
        }

        // Add either pengurus or non-pengurus data
        if (record.isNonPengurus) {
          previewData.isNonPengurus = true
          previewData.namaNonPengurus = record.namaNonPengurus
          previewData.jabatanNonPengurus = record.jabatanNonPengurus
          previewData.npaNonPengurus = record.npaNonPengurus
        } else {
          previewData.perwakilanDinasUserId = record.userId
        }

        // Call preview API
        const response = await suratKeluarAPI.previewPerwakilanDinasPDF(previewData)

        // Revoke previous blob URL if exists
        if (currentBlobUrl) {
          window.URL.revokeObjectURL(currentBlobUrl)
          currentBlobUrl = null
        }

        // Create blob URL for modal preview
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const blobUrl = window.URL.createObjectURL(blob)
        currentBlobUrl = blobUrl // Store for cleanup

        // Get perwakilan name for modal title
        let perwakilanName = 'Perwakilan'
        if (record.isNonPengurus) {
          perwakilanName = record.namaNonPengurus || 'Non-Pengurus'
        } else {
          const pengurusMap = store.state.suratKeluar.pengurusMap || {}
          const perwakilan = pengurusMap[record.userId]
          perwakilanName = perwakilan?.name || 'Perwakilan'
        }

        // IMPORTANT: Close modal first to ensure clean state
        pdfModalVisible.value = false

        // Wait for modal to fully close, then set path and open
        await nextTick()

        pdfModalTitle.value = `Preview Surat Tugas - ${perwakilanName}`
        pdfModalPath.value = blobUrl

        // Small delay to ensure path is set before opening
        await nextTick()

        pdfModalVisible.value = true
      } catch (error) {
        console.error('Preview error:', error)
        notification.error({
          message: 'Gagal membuat preview',
          description:
            error.response?.data?.message || 'Terjadi kesalahan saat membuat preview PDF',
        })
      } finally {
        isLoading.value = false
      }
    }

    // Comprehensive validation function with inline error tracking
    const validateAllFields = () => {
      validationErrors.value = {} // Clear previous errors
      let hasErrors = false

      // Common validation for both Surat Tugas and Surat Lainnya
      if (!form.tanggalSurat) {
        validationErrors.value.tanggalSurat = 'Tanggal surat wajib dipilih'
        hasErrors = true
      }

      if (!form.penandatanganKiriId) {
        validationErrors.value.penandatanganKiri = 'Penandatangan kiri wajib dipilih'
        hasErrors = true
      }

      // Type-specific validation
      if (form.jenisSurat === 'Surat Tugas') {
        // Surat Tugas validation
        if (!form.nomorSurat || form.nomorSurat.trim() === '') {
          validationErrors.value.nomorSurat = 'Nomor surat wajib diisi'
          hasErrors = true
        }

        if (!form.namaKegiatan || form.namaKegiatan.trim() === '') {
          validationErrors.value.namaKegiatan = 'Nama kegiatan wajib diisi'
          hasErrors = true
        }

        if (!form.waktuPerjalananMulai) {
          validationErrors.value.waktuPerjalananMulai = 'Waktu perjalanan mulai wajib diisi'
          hasErrors = true
        }

        if (!form.tempatKegiatan || form.tempatKegiatan.trim() === '') {
          validationErrors.value.tempatKegiatan = 'Tempat kegiatan wajib diisi'
          hasErrors = true
        }

        if (!form.keperluan || form.keperluan.trim() === '') {
          validationErrors.value.keperluan = 'Keperluan wajib diisi'
          hasErrors = true
        }

        // Validate perwakilan dinas
        const validPerwakilan = form.perwakilanDinas.filter(item => {
          if (item.isNonPengurus) {
            // For non-pengurus, check nama is filled
            return item.namaNonPengurus && item.namaNonPengurus.trim() !== ''
          } else {
            // For pengurus, check userId is selected
            return item.userId
          }
        })

        if (validPerwakilan.length === 0) {
          validationErrors.value.perwakilanDinas = 'Minimal harus ada 1 perwakilan dinas'
          hasErrors = true
        }

        // Validate individual non-pengurus fields
        for (const item of form.perwakilanDinas) {
          if (item.isNonPengurus) {
            if (!item.namaNonPengurus || item.namaNonPengurus.trim() === '') {
              validationErrors.value.perwakilanDinas = 'Nama non-pengurus wajib diisi'
              hasErrors = true
              break
            }
            if (!item.jabatanNonPengurus || item.jabatanNonPengurus.trim() === '') {
              validationErrors.value.perwakilanDinas = 'Jabatan non-pengurus wajib diisi'
              hasErrors = true
              break
            }
            if (!item.npaNonPengurus || item.npaNonPengurus.trim() === '') {
              validationErrors.value.perwakilanDinas = 'NPA non-pengurus wajib diisi'
              hasErrors = true
              break
            }
          }
        }
      } else if (form.jenisSurat === 'Lainnya') {
        // Lainnya validation - manual nomor input
        if (!form.nomorSuratManual || form.nomorSuratManual.trim() === '') {
          validationErrors.value.nomorSuratManual = 'Nomor surat wajib diisi'
          hasErrors = true
        }

        // Check for nomor surat uniqueness error
        if (nomorSuratError.value) {
          validationErrors.value.nomorSuratManual = 'Nomor surat sudah digunakan'
          hasErrors = true
        }

        if (!form.perihal || form.perihal.trim() === '') {
          validationErrors.value.perihal = 'Perihal wajib diisi'
          hasErrors = true
        }

        // Check if Upload File method - validate files instead of body
        if (isUploadFile.value) {
          if (uploadedFiles.value.length === 0) {
            validationErrors.value.uploadedFiles = 'Minimal 1 file harus diupload'
            hasErrors = true
          }
        } else {
          if (!form.body || form.body.trim() === '' || form.body === '<p></p>') {
            validationErrors.value.body = 'Isi surat wajib diisi'
            hasErrors = true
          }
        }
      } else {
        // All other types - auto-generated nomor with rich text body
        if (!form.nomorSurat || form.nomorSurat.trim() === '') {
          validationErrors.value.nomorSurat = 'Nomor surat wajib diisi'
          hasErrors = true
        }

        if (!form.perihal || form.perihal.trim() === '') {
          validationErrors.value.perihal = 'Perihal wajib diisi'
          hasErrors = true
        }

        // Check if Upload File method - validate files instead of body
        if (isUploadFile.value) {
          if (uploadedFiles.value.length === 0) {
            validationErrors.value.uploadedFiles = 'Minimal 1 file harus diupload'
            hasErrors = true
          }
        } else {
          if (!form.body || form.body.trim() === '' || form.body === '<p></p>') {
            validationErrors.value.body = 'Isi surat wajib diisi'
            hasErrors = true
          }
        }
      }

      // Arsipkan flow validation (if applicable)
      if (form.tambahSuratType === 'arsipkan' && arsipForm.suratList.length > 0) {
        arsipForm.suratList.forEach((surat, index) => {
          const suratErrors = {}

          if (!surat.nomorSurat || surat.nomorSurat.trim() === '') {
            suratErrors.nomorSurat = 'Nomor surat wajib diisi'
            hasErrors = true
          }

          if (!surat.tanggalSurat) {
            suratErrors.tanggalSurat = 'Tanggal surat wajib diisi'
            hasErrors = true
          }

          if (!surat.jenisSurat || surat.jenisSurat.trim() === '') {
            suratErrors.jenisSurat = 'Jenis surat wajib dipilih'
            hasErrors = true
          }

          if (!surat.bentukSurat || surat.bentukSurat.trim() === '') {
            suratErrors.bentukSurat = 'Bentuk surat wajib dipilih'
            hasErrors = true
          }

          if (!surat.penandatanganId) {
            suratErrors.penandatanganId = 'Penandatangan 1 wajib dipilih'
            hasErrors = true
          }

          if (!surat.statusPengiriman || surat.statusPengiriman.trim() === '') {
            suratErrors.statusPengiriman = 'Status pengiriman wajib dipilih'
            hasErrors = true
          }

          if (Object.keys(suratErrors).length > 0) {
            if (!validationErrors.value.arsipSurat) {
              validationErrors.value.arsipSurat = {}
            }
            validationErrors.value.arsipSurat[index] = suratErrors
          }
        })
      }

      return !hasErrors
    }

    // Validate arsipkan form (only for arsip flow)
    const validateArsipkanFields = () => {
      validationErrors.value = {} // Clear previous errors
      let hasErrors = false

      // Check if there's at least one surat
      if (arsipForm.suratList.length === 0) {
        notification.error({
          message: 'Validasi Gagal',
          description: 'Minimal harus ada 1 surat untuk diarsipkan',
          duration: 5,
        })
        return false
      }

      // Validate each surat in arsipForm
      arsipForm.suratList.forEach((surat, index) => {
        const suratErrors = {}

        // Check if file exists
        if (!surat.fileRef || !surat.fileRef.file) {
          suratErrors.file = 'File surat wajib diupload'
          hasErrors = true
        }

        if (!surat.nomorSurat || surat.nomorSurat.trim() === '') {
          suratErrors.nomorSurat = 'Nomor surat wajib diisi'
          hasErrors = true
        }

        if (!surat.tanggalSurat) {
          suratErrors.tanggalSurat = 'Tanggal surat wajib diisi'
          hasErrors = true
        }

        if (!surat.jenisSurat || surat.jenisSurat.trim() === '') {
          suratErrors.jenisSurat = 'Jenis surat wajib dipilih'
          hasErrors = true
        }

        if (!surat.bentukSurat || surat.bentukSurat.trim() === '') {
          suratErrors.bentukSurat = 'Bentuk surat wajib dipilih'
          hasErrors = true
        }

        if (!surat.penandatanganId) {
          suratErrors.penandatanganId = 'Penandatangan 1 wajib dipilih'
          hasErrors = true
        }

        if (!surat.statusPengiriman || surat.statusPengiriman.trim() === '') {
          suratErrors.statusPengiriman = 'Status pengiriman wajib dipilih'
          hasErrors = true
        }

        if (Object.keys(suratErrors).length > 0) {
          if (!validationErrors.value.arsipSurat) {
            validationErrors.value.arsipSurat = {}
          }
          validationErrors.value.arsipSurat[index] = suratErrors
        }
      })

      return !hasErrors
    }

    // Handle submit button click - show confirmation modal
    const handleSubmit = async () => {
      try {
        // Run comprehensive validation
        if (!validateAllFields()) {
          notification.error({
            message: 'Validasi Gagal',
            description: 'Mohon lengkapi semua field yang wajib diisi',
            duration: 5,
          })
          return
        }

        // Show confirmation modal
        submitModalVisible.value = true
      } catch (error) {
        console.error('Validation error:', error)
        notification.error({
          message: 'Validasi Gagal',
          description: 'Terjadi kesalahan saat validasi form',
          duration: 5,
        })
      }
    }

    // Confirm submit (called from modal)
    const confirmSubmit = async () => {
      try {
        const formData = prepareFormData(false) // isDraft = false

        const response = await suratKeluarAPI.create(formData)

        if (response.data?.status === 'success') {
          submitModalVisible.value = false
          notification.success({ message: 'Surat berhasil dibuat dan dikirim untuk verifikasi' })
          resetUnsavedChanges()
          router.push('/surat-keluar')
        }
      } catch (error) {
        console.error('Error creating surat:', error)
        notification.error({ message: error.response?.data?.message || 'Gagal membuat surat' })
      }
    }

    // Handle save as draft button click - show confirmation modal
    const handleSaveAsDraft = async () => {
      try {
        // Minimal validation for draft - only nomor surat required
        // const nomorSuratValue = form.jenisSurat !== 'Surat Tugas' ? form.nomorSuratLainnya : form.nomorSurat
        // if (!nomorSuratValue) {
        //   notification.error({
        //     message: 'Validasi Gagal',
        //     description: 'Nomor surat harus diisi untuk menyimpan draft',
        //     duration: 5,
        //   })
        //   return
        // }

        // Check for nomor surat error (Lainnya only)
        if (form.jenisSurat !== 'Surat Tugas' && nomorSuratError.value) {
          notification.error({
            message: 'Validasi Gagal',
            description: 'Nomor surat sudah digunakan. Silakan gunakan nomor lain.',
            duration: 5,
          })
          return
        }

        // Show confirmation modal
        draftModalVisible.value = true
      } catch (error) {
        console.error('Validation error:', error)
        notification.error({
          message: 'Validasi Gagal',
          description: 'Terjadi kesalahan saat validasi form',
          duration: 5,
        })
      }
    }

    // Confirm save as draft (called from modal)
    const confirmSaveAsDraft = async () => {
      try {
        const formData = prepareFormData(true) // isDraft = true

        const response = await suratKeluarAPI.create(formData)

        if (response.data?.status === 'success') {
          draftModalVisible.value = false
          notification.success({ message: 'Draft surat berhasil disimpan' })
          const newId = response.data.data.id
          resetUnsavedChanges()
          router.push('/surat-keluar/' + newId)
        }
      } catch (error) {
        console.error('Error saving draft:', error)
        notification.error({ message: error.response?.data?.message || 'Gagal menyimpan draft' })
      }
    }

    // Handle submit for Arsipkan flow
    const handleSubmitArsip = () => {
      // Run arsipkan-specific validation
      if (!validateArsipkanFields()) {
        notification.error({
          message: 'Validasi Gagal',
          description: 'Mohon lengkapi semua field yang wajib diisi pada setiap surat',
          duration: 5,
        })
        return
      }

      // Show confirmation modal
      arsipModalVisible.value = true
    }

    // Confirm and submit arsip
    const confirmSubmitArsip = async () => {
      submitArsipLoading.value = true
      try {
        // Step 1: Create archived surat with signed PDF files
        const formData = new FormData()

        // Add surat files (signed PDFs)
        arsipForm.suratList.forEach(surat => {
          if (surat.fileRef && surat.fileRef.file) {
            formData.append('files', surat.fileRef.file)
          }
        })

        // Build surat data array (without bukti files - those will be uploaded separately)
        const suratDataList = arsipForm.suratList.map((surat, index) => {
          return {
            fileIndex: index,
            jenisSurat: surat.jenisSurat || 'Lainnya',
            nomorSurat: surat.nomorSurat,
            sifatSurat: surat.sifatSurat || 'Biasa',
            bentukSurat: surat.bentukSurat || 'Soft copy',
            tanggalSurat: surat.tanggalSurat ? surat.tanggalSurat.format('DD-MM-YYYY') : '',
            penandatanganId: surat.penandatanganId,
            statusPengiriman: surat.statusPengiriman || 'Belum dikirim',
            perihal: surat.perihal || null,
            suratMasukTerkaitId: surat.suratMasukTerkaitId || null,
          }
        })

        formData.append('suratData', JSON.stringify(suratDataList))

        // Call API to create archived surat
        const response = await suratKeluarAPI.createArchived(formData)

        if (response.data?.status === 'success') {
          const createdSuratList = response.data.data || []

          // Step 2: Upload bukti pengiriman for surats with 'Sudah dikirim' status
          let buktiUploadCount = 0
          for (let i = 0; i < arsipForm.suratList.length; i++) {
            const surat = arsipForm.suratList[i]
            const createdSurat = createdSuratList[i]

            // Only upload bukti if status is 'Sudah dikirim' and has files
            if (
              surat.statusPengiriman === 'Sudah dikirim' &&
              surat.buktiFiles &&
              surat.buktiFiles.length > 0 &&
              createdSurat?.id
            ) {
              const buktiFormData = new FormData()
              surat.buktiFiles.forEach(buktiFile => {
                const actualFile = buktiFile.originFileObj || buktiFile
                buktiFormData.append('bukti_pengiriman', actualFile)
              })

              try {
                await suratKeluarAPI.uploadBuktiPengirimanSurat(createdSurat.id, buktiFormData)
                buktiUploadCount++
              } catch (buktiError) {
                console.error(
                  `Failed to upload bukti for surat ${createdSurat.nomorSurat}:`,
                  buktiError,
                )
                // Continue with other surats even if one fails
              }
            }
          }

          // Show success message
          let description = `${arsipForm.suratList.length} surat keluar berhasil diarsipkan`
          if (buktiUploadCount > 0) {
            description += ` dengan ${buktiUploadCount} bukti pengiriman`
          }

          notification.success({
            message: 'Berhasil',
            description: description,
          })
          resetUnsavedChanges()
          router.push('/surat-keluar')
        }
      } catch (error) {
        console.error('Error submitting arsip:', error)
        notification.error({ message: error.response?.data?.message || 'Gagal menyimpan surat' })
      } finally {
        submitArsipLoading.value = false
        arsipModalVisible.value = false
      }
    }

    // Initialize
    onMounted(async () => {
      try {
        // Fetch pengurus options from store
        await store.dispatch('suratKeluar/fetchPengurusOptions')

        // Fetch surat masuk options for "Surat Undangan Acara" dropdown
        await store.dispatch('suratKeluar/fetchSuratMasukOptions')

        // Check if duplicating from existing surat
        const duplicateFrom = route.query.duplicateFrom
        if (duplicateFrom) {
          // Set flag to prevent watchers from interfering
          isDuplicating.value = true

          await store.dispatch('suratKeluar/fetchSuratDetail', duplicateFrom)
          const sourceSurat = store.state.suratKeluar.currentSurat
          if (sourceSurat) {
            // Populate form with source data (except id and nomor surat)
            form.tambahSuratType = 'buat_baru'

            // Determine if this is Surat Tugas or Lainnya
            // Surat Tugas has specific fields like waktuPerjalanan, tempatKegiatan
            const isSuratTugas = sourceSurat.jenisSurat === 'Surat Tugas'

            if (isSuratTugas) {
              form.jenisSurat = 'Surat Tugas'
              form.namaKegiatan = sourceSurat.namaKegiatan || ''
              form.tempatKegiatan = sourceSurat.tempatKegiatan || ''
              form.keperluan = sourceSurat.keperluan || ''
              // Copy waktu perjalanan if exists
              if (sourceSurat.waktuPerjalanan) {
                form.waktuPerjalanan = dayjs(sourceSurat.waktuPerjalanan)
              }
              // Copy perwakilan dinas structure (without IDs)
              if (sourceSurat.perwakilanDinas && sourceSurat.perwakilanDinas.length > 0) {
                form.perwakilanDinas = sourceSurat.perwakilanDinas.map(pd => {
                  if (pd.isNonPengurus) {
                    // Copy non-pengurus data
                    return {
                      id: generateTempId(),
                      userId: null,
                      isNonPengurus: true,
                      searchValue: pd.namaNonPengurus || '', // searchValue drives the display for non-pengurus
                      namaNonPengurus: pd.namaNonPengurus || null,
                      jabatanNonPengurus: pd.jabatanNonPengurus || null,
                      npaNonPengurus: pd.npaNonPengurus || null,
                    }
                  } else {
                    // Copy pengurus (user) data
                    return {
                      id: generateTempId(),
                      userId: pd.userId || null,
                      isNonPengurus: false,
                      searchValue: pd.user?.name || '', // searchValue drives the display for pengurus
                      namaNonPengurus: null,
                      jabatanNonPengurus: null,
                      npaNonPengurus: null,
                    }
                  }
                })
              }
            } else {
              // Other surat types (NOT Surat Tugas)
              // Preserve the actual jenisSurat value (e.g., "Cabang", "Farmasi", "Internal - Surat", "Lainnya", etc.)
              form.jenisSurat = sourceSurat.jenisSurat || 'Lainnya'

              // For non-Surat Tugas: namaKegiatan from backend is mapped to perihal in frontend
              form.perihal = sourceSurat.namaKegiatan || ''
              form.body = sourceSurat.body || ''

              // Copy Surat Masuk Terkait if exists
              if (sourceSurat.suratMasukTerkaitId) {
                form.suratMasukTerkaitId = sourceSurat.suratMasukTerkaitId
              }
            }

            // Common fields for both types
            form.sifatSurat = sourceSurat.sifatSurat || 'Biasa'
            form.bentukSurat = sourceSurat.bentukSurat || 'Soft copy'
            form.penandatanganKiriId = sourceSurat.penandatanganKiriId
            form.penandatanganKananId = sourceSurat.penandatanganKananId

            // Copy tanggal surat if exists
            if (sourceSurat.tanggalSurat) {
              form.tanggalSurat = dayjs(sourceSurat.tanggalSurat, 'DD-MM-YYYY')
            }

            // Copy verifikator structure (without IDs, maintain order)
            if (sourceSurat.verifikator && sourceSurat.verifikator.length > 0) {
              form.verifikator = sourceSurat.verifikator.map((v, index) => ({
                id: generateTempId(),
                userId: v.userId || null,
                urutan: index + 1,
              }))
            }

            // Fetch new nomor surat (don't copy the old one)
            if (isSuratTugas) {
              await fetchLatestNomorSurat('Surat Tugas')
              form.nomorSurat = nomorSuratRef.value
            } else if (sourceSurat.jenisSurat === 'Lainnya') {
              // For Lainnya, leave nomorSuratManual empty for user to fill
              form.nomorSuratManual = ''
            } else {
              // For other types, fetch latest nomor based on jenis
              await fetchLatestNomorSurat(sourceSurat.jenisSurat)
              form.nomorSurat = nomorSuratRef.value
            }
          }

          // Unset flag after duplication is complete
          // Use nextTick to ensure all reactive updates are processed
          await new Promise(resolve => setTimeout(resolve, 0))
          isDuplicating.value = false
        }
      } finally {
        pageLoading.value = false
      }
    })

    return {
      formRef,
      fileInputRef,
      form,
      arsipForm,
      rules,
      pageLoading,
      isLoading,
      submitArsipLoading,
      isDetecting,
      isDragOver,
      submitLoading,
      draftLoading,
      submitModalVisible,
      draftModalVisible,
      arsipModalVisible,
      validationErrors,
      canDecrement,
      pengurusOptions,
      pengurusOptionsFiltered,
      undanganAcaraOptions,
      nomorSuratError,
      nomorSuratChecking,
      apiKey,
      suratLainnyaInit,

      // PDF Preview Modal
      pdfModalVisible,
      pdfModalTitle,
      pdfModalPath,

      filterOption,
      addPerwakilanRow,
      removePerwakilanRow,
      addVerifikatorRow,
      removeVerifikatorRow,
      incrementNomorSurat,
      decrementNomorSurat,
      checkNomorSuratUnique,
      handlePreviewLainnya,
      handlePreviewPerwakilan,
      beforeUpload,
      handleRemoveFile,
      removeArsipSurat,
      removeFileFromList,
      getFileObjectUrl,
      triggerFileInput,
      handleFileChange,
      handleDragOver,
      handleDragLeave,
      handleFileDrop,
      autoDetectWithAI,
      handleSubmit,
      confirmSubmit,
      handleSaveAsDraft,
      confirmSaveAsDraft,
      handleSubmitArsip,
      confirmSubmitArsip,
      // Bukti modal handlers
      buktiModalVisible,
      currentBuktiFiles,
      openBuktiModal,
      beforeBuktiUpload,
      handleBuktiModalOk,
      handleBuktiModalCancel,

      // Metode Pembuatan
      isNotSuratTugas,
      isUploadFile,
      uploadedFiles,
      metodePembuatanFileInputRef,
      triggerMetodePembuatanFileInput,
      handleMetodePembuatanDragOver,
      handleMetodePembuatanDragLeave,
      handleMetodePembuatanFileDrop,
      handleMetodePembuatanFileChange,
      removeMetodePembuatanUploadedFile,
      getMetodePembuatanFileUrl,
    }
  },
})
</script>

<style lang="scss" scoped>
.surat-keluar-create {
  padding: 24px;
  background: #fcfcfc;
  min-height: calc(100vh - 64px);
  position: relative;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }
}

.page-header {
  margin-bottom: 24px;

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #262626;
    margin: 0;

    @media (max-width: 576px) {
      font-size: 20px;
    }
  }
}

/* --- BUTTON STYLES (ROOT LEVEL) --- */

/* 1. Black Outline Pill (KEMBALI) */
.btn-pill-black {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #262626;
  background: white;
  color: #262626;
  padding: 6px 16px;
  min-height: auto;
  height: 42px; /* Enforced height */
  line-height: 1.5;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: none;

  &:hover:not(:disabled) {
    background: #262626 !important;
    border-color: #262626 !important;
    color: white !important;
  }

  &:disabled {
    border-color: #d9d9d9;
    background: #f5f5f5;
    color: #d9d9d9;
  }
}

/* 2. Red Solid Pill (YA, SIMPAN / Modal Actions) */
.btn-pill-red {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #e10f28;
  background: #e10f28; /* Solid Background */
  color: white; /* White text */
  padding: 6px 16px;
  min-height: auto;
  height: 42px; /* Enforced height */
  line-height: 1.5;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: none;

  &:hover:not(:disabled) {
    background: white !important; /* Darker red on hover */
    border-color: #e10f28 !important;
    color: #e10f28 !important;
  }

  &:disabled {
    border-color: white;
    background: white;
    color: white;
  }
}

/* 3. Red Outline Pill (Tambah Foto) */
.btn-pill-red-outline {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #e10f28;
  background: white;
  color: #e10f28;
  padding: 6px 16px;
  min-height: auto;
  height: 42px; /* Matches standard input height */
  line-height: 1.5;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: none;

  /* Target the Icon specifically to ensure it is red */
  :deep(.anticon) {
    color: #e10f28;
    transition: color 0.3s;
  }

  &:hover:not(:disabled) {
    background: #e10f28 !important; /* Solid red on hover */
    border-color: #e10f28 !important;
    color: white !important;

    /* Force the icon to turn white on hover */
    :deep(.anticon) {
      color: white !important;
    }
  }

  &:focus {
    color: #e10f28;
    border-color: #e10f28;
    background: white;
  }
}

/* --- END BUTTON STYLES --- */

/* Bukti files summary display */
.bukti-files-summary {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f0f9ff;
  border: 1px solid #bae7ff;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;

  .summary-icon {
    color: #1890ff;
    font-size: 14px;
  }

  .summary-text {
    color: #0050b3;
    font-size: 13px;
  }
}

/* Bukti pengiriman modal styles */
.bukti-pengiriman-modal {
  :deep(.ant-upload-list-picture) {
    .ant-upload-list-item {
      padding: 8px;
      border-radius: 4px;
    }
  }
}

.form-card {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }

  // Move required asterisk to the right side of label
  :deep(.ant-form-item-label > label.ant-form-item-required) {
    &::before {
      display: none !important;
    }

    &::after {
      display: inline-block;
      margin-left: 4px;
      color: #ff4d4f;
      font-size: 14px;
      font-family: SimSun, sans-serif;
      line-height: 1;
      content: '*' !important;
      visibility: visible !important;
    }
  }

  /* Radio Button Styles */
  :deep(.ant-radio-checked .ant-radio-inner) {
    border-color: #e10f28 !important;
  }

  :deep(.ant-radio-checked .ant-radio-inner::after) {
    background-color: #e10f28 !important;
  }

  :deep(.ant-radio-wrapper:hover .ant-radio .ant-radio-inner),
  :deep(.ant-radio:hover .ant-radio-inner) {
    border-color: #e10f28 !important;
  }

  :deep(.ant-radio-input:focus + .ant-radio-inner) {
    box-shadow: 0 0 0 3px rgba(225, 15, 40, 0.2);
  }
}

.form-section {
  margin-bottom: 24px;

  .section-label {
    display: block;
    font-weight: 500;
    color: #262626;
    margin-bottom: 8px;
    font-size: 14px;
  }

  .required {
    color: #ff4d4f;
  }

  .section-description {
    font-size: 12px;
    color: #8c8c8c;
    margin-bottom: 8px;
  }
}

.radio-group-horizontal {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;

  :deep(.ant-radio-wrapper) {
    margin-right: 0;
  }
}

.full-width {
  width: 100%;
}

.verifikator-form-item {
  margin-bottom: 4px !important;
}

:deep(.alur-verifikasi-section.alur-verifikasi-tight) {
  margin-top: 0 !important;
}

.preview-section {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;

  .btn-preview {
    border-radius: 50px;
    font-size: 14px;
    font-weight: 700;
    border: 2px solid #e10f28;
    max-width: 230px;
    background: white;
    color: #e10f28;
    padding: 6px 12px;
    min-height: auto;
    height: auto;
    line-height: 1.5;
    transition: all 0.3s;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:hover:not(:disabled) {
      background: #e10f28 !important;
      border-color: white !important;
      color: white !important;
    }

    &:disabled {
      border-color: #98a2b3;
      background: #98a2b3;
      color: white;
    }
  }
}

.submit-section {
  display: flex;
  gap: 16px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    padding-top: 16px;
  }

  &.initial-submit {
    border-top: none;
    padding-top: 0;
  }

  .simpan-button {
    border-radius: 50px;
    font-size: 14px;
    font-weight: 700;
    border: 2px solid #e10f28;
    max-width: 230px;
    background: #e10f28;
    color: white;
    padding: 6px 12px;
    transition: all 0.3s;

    @media (max-width: 768px) {
      width: 100%;
      max-width: 100%;
    }

    &:hover:not(:disabled) {
      background: white !important;
      border-color: #e10f28 !important;
      color: #e10f28 !important;
    }

    &:disabled {
      border-color: #98a2b3;
      background: #98a2b3;
      color: white;
    }
  }

  .btn-draft {
    border-radius: 50px;
    font-size: 14px;
    font-weight: 700;
    border: 2px solid black;
    max-width: 230px;
    background: white;
    color: black;
    padding: 6px 12px;
    transition: all 0.3s;

    @media (max-width: 768px) {
      width: 100%;
      max-width: 100%;
    }

    &:hover:not(:disabled) {
      background: black !important;
      border-color: white !important;
      color: white !important;
    }

    &:disabled {
      border-color: #98a2b3;
      background: #98a2b3;
      color: white;
    }
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

// Upload Area Styles
.upload-area {
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  background: #fafafa;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-color: #e01f48;
    background: #fff9fa;
  }

  &.drag-over {
    border-color: #e01f48;
    background: #fff9fa;
  }

  .upload-icon {
    margin-bottom: 16px;
  }

  .upload-text {
    font-size: 16px;
    font-weight: 500;
    color: #262626;
    margin: 0 0 8px 0;
  }

  .upload-hint {
    font-size: 14px;
    color: #8c8c8c;
    margin: 0;
  }
}

.uploaded-files-list {
  margin-top: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: white;
  padding: 12px;

  .uploaded-file-item {
    display: flex;
    align-items: center;
    padding: 12px;
    border-bottom: 1px solid #f0f0f0;
    gap: 12px;

    &:last-child {
      border-bottom: none;
    }

    .file-icon {
      color: #e01f48;
      font-size: 18px;
      flex-shrink: 0;
    }

    .file-name-link {
      flex: 1;
      color: #1890ff;
      text-decoration: none;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &:hover {
        text-decoration: underline;
      }
    }

    .delete-file-button {
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ff4d4f;
      transition: all 0.2s;

      &:hover {
        background: #fff1f0;
        border-radius: 4px;
      }
    }
  }
}

.ai-section {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;

  .btn-ai-detect {
    background: white;
    border: 2px solid #e10f28;
    color: #e10f28;
    border-radius: 50px;
    padding: 8px 24px;
    height: auto;
    font-weight: 600;
    font-size: 14px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s;

    svg {
      flex-shrink: 0;
    }

    &:hover:not(:disabled) {
      background: #fff1f0;
      border-color: #e10f28;
      color: #e10f28;
    }

    &:disabled {
      border-color: #d9d9d9;
      color: #d9d9d9;
      cursor: not-allowed;
    }
  }

  .info-icon {
    color: #8c8c8c;
    font-size: 16px;
    cursor: help;
  }
}

.ai-loading-section {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px 24px;
  margin-bottom: 24px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;

  .ai-loading-content {
    text-align: center;

    .loading-spinner {
      width: 48px;
      height: 48px;
      border: 4px solid #f0f0f0;
      border-top: 4px solid #e10f28;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 24px;
    }

    .ai-loading-title {
      font-size: 18px;
      font-weight: 600;
      color: #262626;
      margin: 0 0 8px 0;
    }

    .ai-loading-text {
      font-size: 14px;
      color: #8c8c8c;
      margin: 0;
      max-width: 300px;
      line-height: 1.6;
    }
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.uploaded-surat-section {
  background: white;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    padding: 16px;
    margin-bottom: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }

  .surat-header {
    margin-bottom: 16px;

    @media (max-width: 768px) {
      margin-bottom: 12px;
    }

    .surat-title {
      font-size: 18px;
      font-weight: 600;
      color: #262626;
      margin: 0;

      @media (max-width: 576px) {
        font-size: 16px;
      }
    }
  }

  .file-attachment {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    background: #fafafa;
    border-radius: 6px;
    margin-bottom: 24px;

    .attachment-icon {
      color: #e01f48;
      font-size: 18px;
    }

    .file-link {
      color: #1890ff;
      text-decoration: none;
      font-size: 14px;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #262626;
    margin: 0 0 16px 0;
    padding-bottom: 8px;
    border-bottom: 2px solid #f0f0f0;
  }

  .bentuk-surat-option {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }

    .option-description {
      font-size: 13px;
      color: #8c8c8c;
      margin: 4px 0 0 24px;
      line-height: 1.5;
    }
  }

  .hapus-surat-button {
    border: 1px solid #262626;
    background: white;
    color: #262626;
    border-radius: 6px;
    font-weight: 500;
    height: 40px;
    padding: 0 24px;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    transition: all 0.3s;

    &:hover:not(:disabled) {
      border-color: #ff4d4f;
      color: #ff4d4f;
      background: #fff1f0;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    svg {
      flex-shrink: 0;
    }
  }

  :deep(.ant-form-item) {
    margin-bottom: 16px;

    .ant-form-item-label > label {
      font-weight: 500;
      color: #262626;
      font-size: 14px;

      &.ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
        color: #ff4d4f;
      }
    }
  }

  :deep(.ant-radio-group) {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  }

  :deep(.ant-input),
  :deep(.ant-input-number),
  :deep(.ant-select-selector),
  :deep(.ant-picker) {
    font-size: 14px;
  }

  /* Status Pengiriman: Removed nested button styles, as we use global classes now */
  .status-pengiriman-with-button {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
    }

    :deep(.ant-radio-group) {
      flex: 0 0 auto;
    }
  }
}

// TinyMCE Styles
.tinymce-container {
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  overflow: hidden;

  &.has-error {
    border-color: #ff4d4f;
  }

  :deep(.tox-tinymce) {
    border: none;
    border-radius: 8px;
  }

  :deep(.tox .tox-editor-header) {
    background: #fafafa;
    border-bottom: 1px solid #d9d9d9;
  }

  :deep(.tox .tox-edit-area__iframe) {
    background: white;
  }
}

.error-hint {
  display: block;
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.5;
}

:deep(.ant-select-selection-placeholder) {
  color: #798892 !important;
}

:deep(.ant-input::placeholder),
:deep(.ant-input-affix-wrapper input::placeholder),
:deep(.ant-picker-input input::placeholder),
:deep(.ant-input-textarea textarea::placeholder) {
  color: #798892 !important;
}

// Confirmation Modal Styles
.confirm-modal {
  .modal-description {
    color: #595959;
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 32px;
  }

  .modal-buttons {
    display: flex;
    gap: 16px;
    width: 100%;

    .btn-pill-black,
    .btn-pill-red {
      flex: 1;
      width: 100%;
      text-align: center;
    }
  }
}

// Upload Section Styles for Metode Pembuatan
.upload-section {
  margin-bottom: 24px;

  .section-description {
    font-size: 12px;
    color: #8c8c8c;
    margin-bottom: 12px;
  }
}

.upload-area {
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background: #fafafa;

  &:hover,
  &.drag-over {
    border-color: #e10f28;
    background: #fff1f2;
  }

  .upload-icon {
    margin-bottom: 16px;
  }

  .upload-text {
    font-size: 14px;
    color: #262626;
    margin: 0 0 8px 0;
    font-weight: 500;
  }

  .upload-hint {
    font-size: 12px;
    color: #8c8c8c;
    margin: 0;
  }
}

.uploaded-files-list {
  margin-top: 16px;

  .uploaded-file-item {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    background: #f5f5f5;
    border-radius: 6px;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }

    .file-icon {
      color: #8c8c8c;
      margin-right: 8px;
    }

    .file-name-link {
      flex: 1;
      font-size: 14px;
      color: #1890ff;
      text-decoration: none;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &:hover {
        text-decoration: underline;
        color: #40a9ff;
      }
    }

    .btn-remove-file {
      padding: 0 4px;
    }
  }
}
</style>

<style lang="scss">
/* 1. Black Outline Pill */
.btn-pill-black {
  border-radius: 50px !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  border: 2px solid #262626 !important;
  background: white !important;
  color: #262626 !important;
  padding: 6px 20px !important;
  height: 40px !important;
  line-height: 1.5 !important;
  box-shadow: none !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: #262626 !important;
    border-color: #262626 !important;
    color: white !important;
  }
}

/* 2. Red Solid Pill */
.btn-pill-red {
  border-radius: 50px !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  border: 2px solid #e10f28 !important;
  background: #e10f28 !important;
  color: white !important;
  padding: 6px 20px !important;
  height: 40px !important;
  line-height: 1.5 !important;
  box-shadow: none !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: white !important;
    border-color: #e10f28 !important;
    color: #e10f28 !important;
  }

  /* Ensure icon inside is white */
  .anticon {
    color: white !important;
  }
}
</style>
